import React, { useState, useMemo, useCallback, useEffect } from "react";

// ─── Constants ───
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 15 }, (_, i) => i + 8); // 8am–10pm
const HH = 48; // hour height in px
const getMon = (d) => { const dt = new Date(d); const day = dt.getDay(); const diff = dt.getDate() - (day === 0 ? 6 : day - 1); dt.setDate(diff); dt.setHours(0,0,0,0); return dt; };
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const ds = (d) => d.toISOString().split("T")[0];
const fmtDateShort = (d) => new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
const fm = (h, m) => { const hr = h % 12 || 12; const ap = h < 12 ? "a" : "p"; return m ? `${hr}:${String(m).padStart(2,"0")}${ap}` : `${hr}${ap}`; };
const weekKey = (mon) => mon.toISOString().split("T")[0];

// ─── Color Palette ───
const TC = {
  "CIP-Intake/Assessment": { bg: "#14532d", text: "#fff", border: "#166534" },
  "CIP-Individual":        { bg: "#22c55e", text: "#052e16", border: "#16a34a" },
  "SROP-Intake/Assessment": { bg: "#1e40af", text: "#fff", border: "#1d4ed8" },
  "SROP-Individual":        { bg: "#93c5fd", text: "#1e3a5f", border: "#60a5fa" },
  "Group":   { bg: "#0d9488", text: "#fff", border: "#0f766e" },
  "DRI":     { bg: "#dc2626", text: "#fff", border: "#b91c1c" },
  "Blocked": { bg: "#92400e22", text: "#78350f", border: "#a8896a" },
};
const getTC = (a, cls) => {
  if (a.type === "Group") return TC["Group"];
  if (a.type === "DRI") return TC["DRI"];
  if (a.type === "Blocked") return TC["Blocked"];
  const cl = a.client_id ? cls.find(c => c.id === a.client_id) : null;
  const prog = a.program || cl?.program || "CIP";
  const key = `${prog}-${a.type}`;
  return TC[key] || TC["CIP-Individual"];
};

// ─── Status badge colors ───
const SB = { Scheduled: { bg: "#dbeafe", text: "#1e40af" }, Completed: { bg: "#dcfce7", text: "#166534" }, "No-Show": { bg: "#fee2e2", text: "#991b1b" }, Cancelled: { bg: "#f1f5f9", text: "#64748b" }, Rescheduled: { bg: "#fef3c7", text: "#92400e" } };

// ─── Icons ───
const CalI = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const UsI = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const LiI = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>;
const ChI = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>;
const GeI = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>;
const CkI = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>;
const RpI = () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>;
const XIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const PlI = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;

// ─── Shared styles ───
const inp = { width: "100%", padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, outline: "none", boxSizing: "border-box" };
const bp = { padding: "7px 16px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", fontSize: 12, fontWeight: 600 };
const bpP = { ...bp, background: "#6366f1", color: "#fff", border: "1px solid #6366f1" };
const bd = { ...bp, background: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca" };
const bs = { ...bp, background: "#6366f1", color: "#fff", border: "none", fontSize: 11 };
const sl = { ...inp, width: "auto", cursor: "pointer" };

// ─── Modal ───
const Modal = ({ title, onClose, children, width }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
    <div style={{ background: "#fff", borderRadius: 14, width: width || 480, maxWidth: "95vw", maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,.2)" }} onClick={e => e.stopPropagation()}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid #f1f5f9" }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{title}</h3>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><XIcon /></button>
      </div>
      <div style={{ padding: 18 }}>{children}</div>
    </div>
  </div>
);

// ─── Field Label ───
const Fl = ({ l, children }) => <div style={{ marginBottom: 10 }}><label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 3, textTransform: "uppercase" }}>{l}</label>{children}</div>;

// ─── Checkbox ───
const Chk = ({ c, onChange, label }) => <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}><div onClick={onChange} style={{ width: 18, height: 18, borderRadius: 4, border: c ? "none" : "2px solid #cbd5e1", background: c ? "#6366f1" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, color: "#fff" }}>{c && <CkI />}</div>{label && <span>{label}</span>}</label>;

// ─── Appointment Block ───
const AB = ({ a, co, cl, onClick, cls }) => {
  const [hov, setHov] = React.useState(false);
  const t = getTC(a, cls);
  const isB = a.type === "Blocked";
  const top = (a.start_hour - HOURS[0]) * HH + ((a.start_min || 0) / 60) * HH;
  const height = ((a.end_hour - a.start_hour) + ((a.end_min || 0) - (a.start_min || 0)) / 60) * HH;
  return <div onClick={() => onClick(a)} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
    style={{ position: "absolute", top, left: 2, right: 2, height: Math.max(height, 18), borderRadius: 5, background: isB ? "repeating-linear-gradient(135deg,#d4c5a9,#d4c5a9 4px,#e8dcc8 4px,#e8dcc8 8px)" : t.bg, border: `1.5px solid ${t.border}`, borderLeft: `3px solid ${co?.color || t.border}`, padding: "3px 5px", cursor: "pointer", overflow: hov ? "visible" : "hidden", fontSize: 11.5, lineHeight: 1.2, zIndex: hov ? 10 : 2, boxShadow: hov ? "0 3px 12px rgba(0,0,0,.13)" : "none", transform: hov ? "scale(1.02)" : "none", transition: "box-shadow .15s,transform .1s" }}>
    <div style={{ fontWeight: 700, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "flex", alignItems: "center", gap: 2 }}>
      {isB ? a.blocked_reason : a.type === "DRI" ? (a.dri_label || "DRI") : a.type === "Group" ? (a.group_topic || a.group_name) : cl ? `${cl.last_name}, ${cl.first_name}` : "—"}
      {a.recur_series_id && <span style={{ opacity: .5 }}><RpI /></span>}
      {a.attended === 1 && <span style={{ opacity: .7 }}><CkI /></span>}
    </div>
    {!isB && <div style={{ color: t.text, opacity: .8, fontSize: 10 }}>
      {a.program && a.type !== "Group" && a.type !== "DRI" && <span style={{ fontWeight: 800 }}>{a.program} </span>}
      {a.computed_session_num ? <span>S#{a.computed_session_num} </span> : null}
      {fm(a.start_hour, a.start_min || 0)}
      {a.status === "No-Show" && <span style={{ fontWeight: 700 }}> NS</span>}
    </div>}
    {hov && cl && !isB && a.type !== "DRI" && <div style={{ marginTop: 3, padding: "4px 5px", background: "rgba(0,0,0,.78)", borderRadius: 5, color: "#fff", fontSize: 10, lineHeight: 1.3, whiteSpace: "nowrap" }}>
      <div>{cl.phone}</div>
      <div style={{ userSelect: "text" }}>{cl.email}</div>
      {(parseFloat(cl.total_owed||0) - parseFloat(cl.total_paid||0)) > 0 ? <div style={{ color: "#fca5a5", fontWeight: 700 }}>Bal: ${(parseFloat(cl.total_owed||0) - parseFloat(cl.total_paid||0)).toFixed(2)}</div> : <div style={{ color: "#86efac" }}>Bal: $0</div>}
    </div>}
  </div>;
};

// Non-client types
const ncts = ["Blocked", "Group", "DRI"];

// ─── Intake cap helper ───
function getIntakeCap(co, weekStr) {
  if (!co) return 3;
  return co.intakeCaps?.[weekStr] ?? co.default_intake_cap ?? 3;
}

// ════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════
export default function CounselSync({ initialData, api, onLogout }) {
  // ─── Core state ───
  const [cos, setCos] = useState(initialData.counselors || []);
  const [cls, setCls] = useState(initialData.clients || []);
  const [appts, setAppts] = useState([]);
  const [nav, setNav] = useState("calendar");
  const [wo, setWo] = useState(0);
  const [fco, setFco] = useState(initialData.counselors?.[0]?.id || 1);
  const [sel, setSel] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [nw, setNw] = useState(null);
  const [search, setSearch] = useState("");
  const [cf, setCf] = useState("All");
  const [toast, setToast] = useState(null);
  const [clMode, setClMode] = useState("caseload");
  const [showAddCl, setShowAddCl] = useState(null);
  const [showEditCo, setShowEditCo] = useState(null);
  const [showGL, setShowGL] = useState(null);
  const [showCD, setShowCD] = useState(null);
  const [showDelSeries, setShowDelSeries] = useState(null);
  const [showEditUser, setShowEditUser] = useState(null);
  // ─── New Intake Wizard state ───
  const [showIntake, setShowIntake] = useState(false);
  const [intakeStep, setIntakeStep] = useState(1); // 1=filter, 2=pick slot, 3=pick/create client
  const [intakeFilter, setIntakeFilter] = useState('all'); // 'all' | 'day' | 'evening'
  const [intakeSlots, setIntakeSlots] = useState(null); // {results:[{date,counselors:[...]}], searched_days, from_date}
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [intakePicked, setIntakePicked] = useState(null); // {counselor_id, counselor_name, date, slot:{start_hour,start_min,end_hour,end_min}, intake_used, intake_cap}
  const [intakeClientMode, setIntakeClientMode] = useState('existing'); // 'existing' | 'new'
  const [intakeExistingId, setIntakeExistingId] = useState('');
  const [intakeNewClient, setIntakeNewClient] = useState({ first_name:'', last_name:'', phone:'', email:'', program:'CIP', contact_pref:'both', total_owed:0, total_paid:0 });
  const [adTab, setAdTab] = useState("counselors");
  const [remCfg, setRemCfg] = useState(initialData.reminderConfig || []);
  const [intakeReminders, setIntakeReminders] = useState([]);
  const [sendConfirmation, setSendConfirmation] = useState(true);
  const [clFCo, setClFCo] = useState("all");
  const [clFPr, setClFPr] = useState("all");
  const [gTopics, setGTopics] = useState(initialData.globalTopics || []);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({ smtp_host: '', smtp_port: '587', smtp_user: '', smtp_pass: '', smtp_from: '', smtp_enabled: false, textmagic_user: '', textmagic_key: '', textmagic_enabled: false });
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [capsWo, setCapsWo] = useState(0);
  // Reports state
  const [reportSubTab, setReportSubTab] = useState("caseload");
  const [weeklySummary, setWeeklySummary] = useState([]);
  const [summaryWeek, setSummaryWeek] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  // Available Intakes report state
  const [availIntakes, setAvailIntakes] = useState(null); // flattened rows or null = not loaded
  const [availIntakesLoading, setAvailIntakesLoading] = useState(false);
  const [availIntakesSort, setAvailIntakesSort] = useState({ key: "date", dir: "asc" });
  const [availIntakesFilterCo, setAvailIntakesFilterCo] = useState("all");

  // ─── User / Role ───
  const user = initialData.user || null;
  const role = user?.role || "counselor";
  const isClinDir = role === "clinical_director" || role === "owner";
  const isOpsDir = role === "ops_director" || role === "owner";
  const isAdmin = role === "admin" || isClinDir || isOpsDir;
  const isOwner = role === "owner";
  const canSeeAll = isClinDir;

  const st = useCallback((m, t = "success") => { setToast({ m, t }); setTimeout(() => setToast(null), 4000); }, []);

  // ─── Dates ───
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const monday = useMemo(() => { const m = getMon(today); m.setDate(m.getDate() + wo * 7); return m; }, [wo, today]);
  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(monday, i)), [monday]);
  const sd = ds(weekDates[0]), ed = ds(weekDates[6]);

  // ─── Data loading ───
  const reload = useCallback(async () => {
    try { setAppts(await api.getAppointments({ counselor_id: fco, start_date: sd, end_date: ed })); } catch (e) { console.error(e); }
  }, [api, fco, sd, ed]);
  useEffect(() => { reload(); }, [reload]);

  const reloadCos = useCallback(async () => { try { setCos(await api.getCounselors()); } catch (e) {} }, [api]);
  const reloadCls = useCallback(async () => { try { setCls(await api.getClients()); } catch (e) {} }, [api]);
  const reloadIntakeReminders = useCallback(async () => {
    try { setIntakeReminders(await api.getIntakeReminders()); } catch (e) { console.error("Load intake reminders:", e); }
  }, [api]);
  useEffect(() => { reloadIntakeReminders(); }, [reloadIntakeReminders]);

  // ─── Load users + settings (owner only) ───
  const loadUsers = useCallback(async () => {
    if (!isOwner) return;
    try { setUsers(await api.getUsers()); } catch (e) {}
    try { setSettings(await api.getSettings()); } catch (e) {}
  }, [api, isOwner]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Poll every 60s
  useEffect(() => { const iv = setInterval(() => { reloadCls(); reloadCos(); reload(); }, 60000); return () => clearInterval(iv); }, [reloadCls, reloadCos, reload]);

  // ─── Derived data ───
  const curCo = cos.find(c => c.id === fco);
  const iCnt = useMemo(() => appts.filter(a => a.type === "Intake/Assessment" && a.status !== "Cancelled").length, [appts]);
  const wlCnt = cls.filter(c => c.status === "Waitlist").length;
  const caseloadCls = useMemo(() => cls.filter(c => c.counselor_id === fco && c.status === "Active"), [cls, fco]);
  const allActiveCls = useMemo(() => cls.filter(c => c.status === "Active" || c.status === "Waitlist"), [cls]);
  const filteredAppts = useMemo(() => {
    if (fco === "all") return appts;
    return appts.filter(a => a.counselor_id === fco);
  }, [appts, fco]);

  // ─── Weekly Summary fetch ───
  const fetchWeeklySummary = useCallback(async (weekStart) => {
    setSummaryLoading(true);
    try {
      const cid = (user && role === "counselor") ? user.counselor_id : null;
      const data = await api.getWeeklySummary(weekStart, cid);
      // Ensure ALL counselors appear, even if they had no appointments that week
      const empty = { individual: { scheduled_hours: 0, attended_hours: 0, no_show_hours: 0 }, intake: { scheduled_hours: 0, attended_hours: 0, no_show_hours: 0 }, group: { total_hours: 0 }, dri: { total_hours: 0 } };
      const byId = {};
      (data || []).forEach(s => { byId[s.counselor_id] = s; });
      const targetCos = cid ? cos.filter(c => c.id === cid) : cos;
      const merged = targetCos.map(c => byId[c.id] || { counselor_id: c.id, counselor_name: c.name, ...JSON.parse(JSON.stringify(empty)) });
      setWeeklySummary(merged);
    } catch (e) { console.error("Weekly summary failed:", e); setWeeklySummary([]); }
    setSummaryLoading(false);
  }, [api, user, role, cos]);

  useEffect(() => {
    if (reportSubTab === "summary") {
      const wk = summaryWeek || weekKey(monday);
      if (!summaryWeek) setSummaryWeek(wk);
      fetchWeeklySummary(wk);
    }
  }, [reportSubTab, summaryWeek, fetchWeeklySummary, monday]);

  // ─── Available Intakes fetch ───
  const fetchAvailableIntakes = useCallback(async () => {
    setAvailIntakesLoading(true);
    try {
      const data = await api.getIntakeSlots({ from_date: ds(today), filter: "all" });
      // Flatten the day->counselor->slots tree into one row per slot
      const flat = [];
      (data.results || []).forEach(day => {
        day.counselors.forEach(co => {
          co.slots.forEach(s => {
            flat.push({
              date: day.date,
              counselor_id: co.counselor_id,
              counselor_name: co.counselor_name,
              color: co.color,
              slot: s,
              intake_used: co.intake_used,
              intake_cap: co.intake_cap,
              groups: co.groups || [],
            });
          });
        });
      });
      setAvailIntakes(flat);
    } catch (e) {
      console.error("Available intakes failed:", e);
      st(e.message || "Failed to load available intakes", "warning");
      setAvailIntakes([]);
    }
    setAvailIntakesLoading(false);
  }, [api, today, st]);

  useEffect(() => {
    if (reportSubTab === "available-intakes" && availIntakes === null) {
      fetchAvailableIntakes();
    }
  }, [reportSubTab, availIntakes, fetchAvailableIntakes]);

  // ─── CRUD ───
  // ─── After scheduling any client appointment, navigate to that counselor's week ───
  const jumpToAppointment = (counselorId, dateStr) => {
    if (counselorId && counselorId !== fco) setFco(Number(counselorId));
    if (dateStr) {
      const target = new Date(dateStr + "T12:00:00");
      const targetMon = getMon(target);
      const thisMon = getMon(today);
      const offset = Math.round((targetMon - thisMon) / (7 * 86400000));
      setWo(offset);
    }
    setNav("calendar");
  };

  const saveAppt = async (a) => {
    setSaving(true);
    try {
      const payload = {
        client_id: a.client_id || null, counselor_id: a.counselor_id || fco, type: a.type,
        program: a.program || null, date: a.date,
        start_hour: a.start_hour, start_min: a.start_min || 0,
        end_hour: a.end_hour, end_min: a.end_min || 0,
        status: a.status || "Scheduled", attended: a.attended ?? null,
        notes: a.notes || "", session_num: a.session_num ?? null,
        recurring: a.recurring || false, recur_pattern: a.recur_pattern || null,
        recur_end_date: a.recur_end_date || null,
        blocked_reason: a.blocked_reason || null, dri_label: a.dri_label || null,
        group_name: a.group_name || null, group_topic: a.group_topic || null,
        groupClients: a.groupClients || [], groupAttendance: a.groupAttendance || {},
      };
      if (a.id) { await api.updateAppointment(a.id, payload); st("Appointment updated"); }
      else {
        const result = await api.createAppointment(payload);
        st("Appointment created");
        // Send confirmation if enabled and this is a client-facing appointment with a client
        if (sendConfirmation && result?.id && a.client_id && a.type !== "Blocked" && a.type !== "DRI" && a.type !== "Group") {
          try {
            const conf = await api.sendConfirmation(result.id);
            if (conf?.sent_email || conf?.sent_sms) {
              st(`Confirmation sent (${[conf.sent_email && "email", conf.sent_sms && "text"].filter(Boolean).join(" + ")})`, "info");
            } else if (conf?.skipped_reason) {
              st(`Confirmation not sent: ${conf.skipped_reason}`, "info");
            }
          } catch (ce) { st("Appointment created, but confirmation failed: " + (ce.message || "error"), "warning"); }
        }
      }
      setSel(null); setShowNew(false); setNw(null);
      // If this was an intake (or anything that consumes intake capacity), invalidate the available-intakes report cache
      if (payload.type === "Intake/Assessment") setAvailIntakes(null);
      // Jump to the appointment's counselor + week so the user can see it on the calendar.
      // Done for all appointment types (Blocked / DRI / Group included) so editing a Group to a new week also pivots the view.
      jumpToAppointment(payload.counselor_id, payload.date);
      reload();
    } catch (e) { st(e.message || "Save failed", "warning"); }
    setSaving(false);
  };

  const delAppt = async (id, opts) => {
    if (!window.confirm("Delete this appointment?")) return;
    try { await api.deleteAppointment(id, opts); st("Deleted"); setSel(null); reload(); }
    catch (e) { st("Delete failed", "warning"); }
  };

  const markNoShow = async (a) => {
    if (!window.confirm(`Mark ${a.type} as No-Show?`)) return;
    try { await api.updateAppointment(a.id, { ...a, status: "No-Show", attended: 0 }); st("Marked No-Show"); setSel(null); reload(); }
    catch (e) { st("Failed", "warning"); }
  };

  const markAttended = async (a) => {
    try { await api.updateAppointment(a.id, { ...a, attended: a.attended ? 0 : 1, status: a.attended ? "Scheduled" : "Completed" }); st(a.attended ? "Unmarked" : "Marked attended"); setSel(null); reload(); }
    catch (e) { st("Failed", "warning"); }
  };

  // ─── New appointment helper ───
  const openNew = (dayIdx, hour) => {
    const d = weekDates[dayIdx];
    const defDur = { Individual: 1, "Intake/Assessment": 1.5, Group: 4, DRI: 6, Blocked: 1 };
    setSendConfirmation(true);
    setNw({
      type: "Individual", program: "CIP", date: ds(d), counselor_id: fco,
      start_hour: hour, start_min: 0, end_hour: hour + 1, end_min: 0,
      notes: "", recurring: false, recur_pattern: "weekly", recur_end_date: "",
      blocked_reason: "Lunch", dri_label: "", group_name: "", group_topic: "",
      groupClients: [], groupAttendance: {},
    });
    setShowNew(true);
  };

  // ─── New Intake wizard helpers ───
  const openIntake = (preset) => {
    setShowIntake(true);
    setIntakeFilter('all');
    setIntakeSlots(null);
    setIntakeClientMode('existing');
    setIntakeExistingId('');
    setIntakeNewClient({ first_name:'', last_name:'', phone:'', email:'', program:'CIP', contact_pref:'both', total_owed:0, total_paid:0 });
    setSendConfirmation(true);
    if (preset && preset.counselor_id && preset.date && preset.slot) {
      setIntakePicked(preset);
      setIntakeStep(3);
    } else {
      setIntakePicked(null);
      setIntakeStep(1);
    }
  };
  const closeIntake = () => { setShowIntake(false); };
  const searchIntakeSlots = async (filterVal) => {
    setIntakeLoading(true);
    try {
      const params = { from_date: ds(today), filter: filterVal };
      const data = await api.getIntakeSlots(params);
      setIntakeSlots(data);
      setIntakeStep(2);
    } catch (e) {
      st(e.message || 'Failed to load intake slots', 'warning');
    }
    setIntakeLoading(false);
  };
  const confirmIntake = async () => {
    if (!intakePicked) { st('Pick a time slot first', 'warning'); return; }
    setSaving(true);
    try {
      // Resolve / create client
      let clientId = null, clientProgram = null;
      if (intakeClientMode === 'existing') {
        if (!intakeExistingId) { st('Pick a client', 'warning'); setSaving(false); return; }
        clientId = Number(intakeExistingId);
        const cl = cls.find(c => c.id === clientId);
        clientProgram = cl?.program || 'CIP';
      } else {
        const nc = intakeNewClient;
        if (!nc.first_name?.trim() || !nc.last_name?.trim()) { st('First and last name required', 'warning'); setSaving(false); return; }
        const created = await api.createClient({
          first_name: nc.first_name.trim(), last_name: nc.last_name.trim(),
          phone: nc.phone || '', email: nc.email || '',
          program: nc.program || 'CIP', status: 'Active',
          counselor_id: intakePicked.counselor_id,
          contact_pref: nc.contact_pref || 'both',
          total_owed: Number(nc.total_owed) || 0, total_paid: Number(nc.total_paid) || 0,
          session_num: 0,
        });
        clientId = created.id;
        clientProgram = nc.program || 'CIP';
        await reloadCls();
      }
      // Create the intake appointment
      const apptPayload = {
        client_id: clientId,
        counselor_id: intakePicked.counselor_id,
        type: 'Intake/Assessment',
        program: clientProgram,
        date: intakePicked.date,
        start_hour: intakePicked.slot.start_hour, start_min: intakePicked.slot.start_min || 0,
        end_hour: intakePicked.slot.end_hour, end_min: intakePicked.slot.end_min || 0,
        status: 'Scheduled', attended: null,
        notes: '', session_num: null,
        recurring: false, recur_pattern: null, recur_end_date: null,
        blocked_reason: null, dri_label: null,
        group_name: null, group_topic: null,
        groupClients: [], groupAttendance: {},
      };
      const apptRes = await api.createAppointment(apptPayload);
      st('Intake scheduled');
      // Send confirmation only if the user opted in via the Step 3 checkbox
      if (sendConfirmation && apptRes?.id) {
        try {
          const conf = await api.sendConfirmation(apptRes.id);
          if (conf?.sent_email || conf?.sent_sms) {
            st(`Confirmation sent (${[conf.sent_email && 'email', conf.sent_sms && 'text'].filter(Boolean).join(' + ')})`, 'info');
          } else if (conf?.skipped_reason) {
            st(`Confirmation not sent: ${conf.skipped_reason}`, 'info');
          }
        } catch (ce) { st('Intake scheduled, but confirmation failed: ' + (ce.message || 'error'), 'warning'); }
      }
      // Jump to that counselor's calendar week so the user can see the new appointment
      jumpToAppointment(intakePicked.counselor_id, intakePicked.date);
      // Available-intakes report data is now stale; clear cache so it re-fetches next view
      setAvailIntakes(null);
      closeIntake();
      reload();
    } catch (e) {
      st(e.message || 'Failed to schedule intake', 'warning');
    }
    setSaving(false);
  };

  // ─── Keyboard shortcuts ───
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
      if (e.key === "Escape") { setSel(null); setShowNew(false); setShowAddCl(null); setShowEditCo(null); }
      if (e.key === "n" && !e.ctrlKey && !e.metaKey && nav === "calendar" && !sel && !showNew) { e.preventDefault(); openNew(0, 9); }
      if (e.key === "ArrowLeft" && !sel && !showNew) setWo(o => o - 1);
      if (e.key === "ArrowRight" && !sel && !showNew) setWo(o => o + 1);
      if (e.key === "t" && !e.ctrlKey && !e.metaKey && !sel && !showNew) setWo(0);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [nav, sel, showNew]);

  // ─── Nav items ───
  const navItems = [
    { id: "calendar", ic: <CalI />, l: "Calendar" },
    { id: "clients", ic: <UsI />, l: "Clients" },
    { id: "waitlist", ic: <LiI />, l: "Waitlist", badge: wlCnt },
    { id: "reports", ic: <ChI />, l: "Reports" },
    ...(role !== "counselor" ? [{ id: "admin", ic: <GeI />, l: "Admin" }] : []),
  ];

// ─── Resolve work hours for a date ───
function resolveHours(co, dateStr) {
  if (!co) return { start: 8, startMin: 0, end: 17, endMin: 0 };
  const d = new Date(dateStr + "T12:00:00");
  const dayName = DAYS[d.getDay() === 0 ? 6 : d.getDay() - 1]; // Mon=0 ... Sun=6
  // Check overrides
  if (co.hoursOverrides && co.hoursOverrides.length > 0) {
    const matching = co.hoursOverrides.filter(o => o.day === dayName && o.effectiveDate <= dateStr).sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
    if (matching.length > 0) {
      const ov = matching[0];
      return { start: ov.start, startMin: ov.startMin || 0, end: ov.end, endMin: ov.endMin || 0 };
    }
  }
  // Default hours
  const dh = co.hours?.[dayName];
  if (dh) return { start: dh.start, startMin: dh.startMin || 0, end: dh.end, endMin: dh.endMin || 0 };
  return { start: 8, startMin: 0, end: 17, endMin: 0 };
}

  // ════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════
  return (
    <div style={{ fontFamily: "'DM Sans',system-ui,sans-serif", background: "#f8fafc", minHeight: "100vh" }}>
      {/* Print stylesheet */}
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff; } }`}</style>

      {/* ─── Top Nav ─── */}
      <div className="no-print" style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "0 16px", display: "flex", alignItems: "center", height: 52, gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", marginRight: 16 }}>
          <img src="/logo.png" alt="Midwest ADP - CounselSync" style={{ height: 36, width: "auto", display: "block" }} />
        </div>
        {navItems.map(n => (
          <button key={n.id} onClick={() => setNav(n.id)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 7, border: "none", background: nav === n.id ? "#f0f0ff" : "transparent", color: nav === n.id ? "#6366f1" : "#64748b", fontWeight: 600, fontSize: 12.5, cursor: "pointer", position: "relative" }}>
            {n.ic}<span>{n.l}</span>
            {n.badge > 0 && <span style={{ background: "#dc2626", color: "#fff", borderRadius: 10, padding: "1px 5px", fontSize: 9, fontWeight: 800, marginLeft: 2 }}>{n.badge}</span>}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {user && <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "#64748b" }}>{user.name}</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "#f0f0ff", color: "#6366f1", textTransform: "uppercase" }}>{role.replace("_", " ")}</span>
          <button onClick={onLogout} style={{ ...bp, fontSize: 11, padding: "4px 10px" }}>Logout</button>
        </div>}
      </div>

      <div style={{ padding: "16px 20px", maxWidth: 1400, margin: "0 auto" }}>

      {/* ════════════════════════════════════════ */}
      {/*  CALENDAR TAB                           */}
      {/* ════════════════════════════════════════ */}
      {nav === "calendar" && (
        <div>
          {/* Week nav + counselor filter */}
          <div className="no-print" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <button onClick={() => setWo(o => o - 1)} style={{ ...bp, padding: "5px 10px" }}>←</button>
            <span style={{ fontWeight: 700, fontSize: 14, minWidth: 180, textAlign: "center" }}>
              {fmtDateShort(sd)} — {fmtDateShort(ed)}
            </span>
            <button onClick={() => setWo(o => o + 1)} style={{ ...bp, padding: "5px 10px" }}>→</button>
            <button onClick={() => setWo(0)} style={{ ...bp, fontSize: 11 }}>Today</button>
            <select value={fco} onChange={e => setFco(e.target.value === "all" ? "all" : Number(e.target.value))} style={{ ...inp, width: "auto", minWidth: 160 }}>
              <option value="all">All Counselors</option>
              {cos.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {fco !== "all" && curCo && (() => {
              const used = appts.filter(a => a.counselor_id === fco && a.type === "Intake/Assessment" && a.status !== "Cancelled" && a.status !== "Rescheduled").length;
              const cap = getIntakeCap(curCo, sd);
              const remaining = Math.max(0, cap - used);
              const atCap = used >= cap;
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 7, background: atCap ? "#fee2e2" : remaining === 0 ? "#fef3c7" : "#dcfce7", border: `1px solid ${atCap ? "#fecaca" : remaining === 0 ? "#fde68a" : "#bbf7d0"}`, fontSize: 11.5, fontWeight: 600, color: atCap ? "#991b1b" : remaining === 0 ? "#92400e" : "#166534" }}>
                  <span style={{ textTransform: "uppercase", fontSize: 10, opacity: .75 }}>Intakes</span>
                  <span>{used} of {cap} used</span>
                  <span style={{ opacity: .55 }}>·</span>
                  <span>{remaining} available</span>
                </div>
              );
            })()}
            <div style={{ flex: 1 }} />
            <button onClick={() => openIntake()} style={bp}>+ New Intake</button>
            <button onClick={() => openNew(0, 9)} style={bpP}>+ New Appointment</button>
          </div>

          {/* Calendar grid — 7 day */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            {/* Day headers */}
            <div style={{ display: "grid", gridTemplateColumns: "54px repeat(7, 1fr)", borderBottom: "1px solid #e2e8f0" }}>
              <div />
              {weekDates.map((d, di) => {
                const isT = d.toDateString() === today.toDateString();
                const isWeekend = di >= 5;
                const dayName = DAYS[di];
                const dh = fco !== "all" ? resolveHours(curCo, ds(d)) : null;
                return <div key={di} style={{ padding: "6px 4px", textAlign: "center", borderLeft: "1px solid #f1f5f9", background: isT ? "#f0f0ff" : isWeekend ? "#fafaf8" : "transparent" }}>
                  <div style={{ fontSize: 10.5, color: isWeekend ? "#a3a3a3" : "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>{dayName}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: isT ? "#fff" : "#0f172a", ...(isT ? { background: "#6366f1", width: 28, height: 28, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 14 } : {}) }}>{d.getDate()}</div>
                  {dh && <div style={{ fontSize: 9.5, color: "#94a3b8", marginTop: 1 }}>{fm(dh.start, dh.startMin)}–{fm(dh.end, dh.endMin)}</div>}
                </div>;
              })}
            </div>
            {/* Time grid */}
            <div style={{ display: "grid", gridTemplateColumns: "54px repeat(7, 1fr)", maxHeight: HOURS.length * HH + 20, overflowY: "auto", position: "relative" }}>
              {/* Hour labels */}
              <div>
                {HOURS.map(h => <div key={h} style={{ height: HH, display: "flex", alignItems: "flex-start", justifyContent: "flex-end", paddingRight: 6 }}>
                  <span style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 500, transform: "translateY(-5px)" }}>{fm(h, 0)}</span>
                </div>)}
              </div>
              {/* Day columns */}
              {weekDates.map((wd, di) => {
                const dStr = ds(wd);
                const da = filteredAppts.filter(a => a.date === dStr);
                const isT = wd.toDateString() === today.toDateString();
                const isWeekend = di >= 5;
                const singleCo = fco !== "all" ? curCo : null;
                const dh = singleCo ? resolveHours(singleCo, dStr) : null;
                return <div key={di} style={{ position: "relative", borderLeft: "1px solid #f1f5f9", background: isT ? "#fafaff" : isWeekend ? "#fafaf8" : "transparent" }}>
                  {/* Work hours shading */}
                  {dh && (<>
                    {dh.start > HOURS[0] && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: (dh.start + (dh.startMin || 0)/60 - HOURS[0]) * HH, background: "repeating-linear-gradient(45deg,transparent,transparent 5px,#f1f5f9 5px,#f1f5f9 6px)", zIndex: 1, pointerEvents: "none", opacity: .6 }} />}
                    {dh.end < HOURS[HOURS.length - 1] + 1 && <div style={{ position: "absolute", top: (dh.end + (dh.endMin || 0)/60 - HOURS[0]) * HH, left: 0, right: 0, height: (HOURS[HOURS.length - 1] + 1 - dh.end - (dh.endMin || 0)/60) * HH, background: "repeating-linear-gradient(45deg,transparent,transparent 5px,#f1f5f9 5px,#f1f5f9 6px)", zIndex: 1, pointerEvents: "none", opacity: .6 }} />}
                  </>)}
                  {/* Current time indicator */}
                  {isT && (() => {
                    const now = new Date();
                    const nh = now.getHours(), nm = now.getMinutes();
                    if (nh >= HOURS[0] && nh <= HOURS[HOURS.length - 1]) {
                      const top = (nh - HOURS[0]) * HH + (nm / 60) * HH;
                      return <div style={{ position: "absolute", top, left: 0, right: 0, height: 2, background: "#ef4444", zIndex: 5, pointerEvents: "none" }}>
                        <div style={{ position: "absolute", left: -3, top: -3, width: 8, height: 8, borderRadius: "50%", background: "#ef4444" }} />
                      </div>;
                    }
                    return null;
                  })()}
                  {/* Clickable hour slots */}
                  {HOURS.map(h => <div key={h} onClick={() => openNew(di, h)} style={{ height: HH, borderBottom: "1px solid #f1f5f9", cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"} />)}
                  {/* Appointment blocks */}
                  {da.map(a => {
                    const co = cos.find(c => c.id === a.counselor_id);
                    const cl = a.client_id ? cls.find(c => c.id === a.client_id) : null;
                    return <AB key={a.id} a={a} co={co} cl={cl} onClick={setSel} cls={cls} />;
                  })}
                </div>;
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="no-print" style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", fontSize: 10 }}>
            {Object.entries(TC).filter(([k]) => k !== "Blocked").map(([k, v]) => (
              <span key={k} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: v.bg, border: `1px solid ${v.border}` }} />
                <span style={{ color: "#64748b", fontWeight: 600 }}>{k.replace("-", " ")}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════ */}
      {/*  CLIENTS TAB                            */}
      {/* ════════════════════════════════════════ */}
      {nav === "clients" && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..." style={{ ...inp, width: 220 }} />
            <select value={cf} onChange={e => setCf(e.target.value)} style={{ ...inp, width: "auto" }}>
              {["All", "Active", "Waitlist", "Discharged", "Inactive"].map(s => <option key={s}>{s}</option>)}
            </select>
            <select value={clFCo} onChange={e => setClFCo(e.target.value)} style={{ ...inp, width: "auto" }}>
              <option value="all">All Counselors</option>
              {cos.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={clFPr} onChange={e => setClFPr(e.target.value)} style={{ ...inp, width: "auto" }}>
              <option value="all">All Programs</option>
              <option value="CIP">CIP</option>
              <option value="SROP">SROP</option>
            </select>
            <div style={{ flex: 1 }} />
            <button onClick={() => setShowAddCl({ program: "CIP", status: "Active", contact_pref: "both", total_owed: 0, total_paid: 0 })} style={bp}>+ Add Client</button>
            <button onClick={() => openIntake()} style={bpP}>+ New Intake</button>
          </div>
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead><tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                {["Name", "Phone", "Email", "Program", "Counselor", "Status", "Balance", "Contact Pref"].map(h => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, fontSize: 10.5, textTransform: "uppercase", color: "#64748b" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{cls
                .filter(c => (cf === "All" || c.status === cf))
                .filter(c => !search || `${c.first_name} ${c.last_name}`.toLowerCase().includes(search.toLowerCase()))
                .filter(c => clFCo === "all" || c.counselor_id === Number(clFCo))
                .filter(c => clFPr === "all" || c.program === clFPr)
                .map(c => {
                  const co = cos.find(x => x.id === c.counselor_id);
                  const bal = parseFloat(c.total_owed || 0) - parseFloat(c.total_paid || 0);
                  return <tr key={c.id} onClick={() => setShowCD(c)} style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#fafafa"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <td style={{ padding: "8px 10px", fontWeight: 600 }}>{c.last_name}, {c.first_name}</td>
                    <td style={{ padding: "8px 10px" }}>{c.phone}</td>
                    <td style={{ padding: "8px 10px", userSelect: "text" }}>{c.email}</td>
                    <td style={{ padding: "8px 10px" }}><span style={{ padding: "1px 6px", borderRadius: 3, fontSize: 10, fontWeight: 700, background: c.program === "CIP" ? "#dcfce7" : "#dbeafe", color: c.program === "CIP" ? "#166534" : "#1e40af" }}>{c.program}</span></td>
                    <td style={{ padding: "8px 10px" }}>{co ? <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: co.color }} />{co.name}</span> : "—"}</td>
                    <td style={{ padding: "8px 10px" }}><span style={{ padding: "1px 6px", borderRadius: 3, fontSize: 10, fontWeight: 600, background: c.status === "Active" ? "#dcfce7" : c.status === "Waitlist" ? "#fef3c7" : "#f1f5f9", color: c.status === "Active" ? "#166534" : c.status === "Waitlist" ? "#92400e" : "#64748b" }}>{c.status}</span></td>
                    <td style={{ padding: "8px 10px", fontWeight: bal > 0 ? 700 : 400, color: bal > 0 ? "#dc2626" : "#059669" }}>{bal > 0 ? `$${bal.toFixed(2)}` : "$0"}</td>
                    <td style={{ padding: "8px 10px", fontSize: 11 }}>{c.contact_pref || "—"}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════ */}
      {/*  WAITLIST TAB                           */}
      {/* ════════════════════════════════════════ */}
      {nav === "waitlist" && (
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 800, marginBottom: 12 }}>Waitlist</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {cls.filter(c => c.status === "Waitlist").sort((a, b) => {
              const po = { High: 0, Medium: 1, Low: 2 };
              return (po[a.waitlist_priority] ?? 1) - (po[b.waitlist_priority] ?? 1);
            }).map(c => {
              const daysWaiting = c.waitlist_date ? Math.floor((Date.now() - new Date(c.waitlist_date + "T00:00:00").getTime()) / 86400000) : "?";
              return <div key={c.id} style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: 14, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{c.last_name}, {c.first_name}</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>{c.program} · {c.phone} · Waiting {daysWaiting} days</div>
                  {c.waitlist_notes && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{c.waitlist_notes}</div>}
                </div>
                <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: c.waitlist_priority === "High" ? "#fee2e2" : c.waitlist_priority === "Low" ? "#f1f5f9" : "#fef3c7", color: c.waitlist_priority === "High" ? "#991b1b" : c.waitlist_priority === "Low" ? "#64748b" : "#92400e" }}>{c.waitlist_priority || "Medium"}</span>
                <button onClick={() => { setNav("calendar"); setShowNew(true); setNw({ type: "Intake/Assessment", program: c.program, client_id: c.id, counselor_id: fco, date: ds(today), start_hour: 9, start_min: 0, end_hour: 10, end_min: 30, notes: "", recurring: false }); }} style={bpP}>Schedule Intake</button>
              </div>;
            })}
            {cls.filter(c => c.status === "Waitlist").length === 0 && <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>No clients on waitlist</div>}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════ */}
      {/*  REPORTS TAB                            */}
      {/* ════════════════════════════════════════ */}
      {nav === "reports" && (
        <div>
          {/* Sub-tab selector */}
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            {["caseload", "summary", "available-intakes"].map(stt => (
              <button key={stt} onClick={() => { setReportSubTab(stt); if (stt === "summary" && !summaryWeek) setSummaryWeek(weekKey(monday)); }}
                style={{ padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 700,
                  border: reportSubTab === stt ? "2px solid #6366f1" : "1px solid #e2e8f0",
                  background: reportSubTab === stt ? "#f0f0ff" : "#fff",
                  color: reportSubTab === stt ? "#6366f1" : "#475569", cursor: "pointer" }}>
                {stt === "caseload" ? "Caseload" : stt === "summary" ? "Weekly Summary" : "Available Intakes"}
              </button>
            ))}
          </div>

          {reportSubTab === "caseload" && (
            <div>
              {/* Summary cards */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
                {[
                  { label: "Total Appts (Week)", value: appts.filter(a => a.type !== "Blocked").length, color: "#2563eb" },
                  { label: "No-Show Rate", value: (() => { const total = appts.filter(a => a.type !== "Blocked" && a.type !== "DRI").length; const ns = appts.filter(a => a.status === "No-Show").length; return total > 0 ? `${((ns / total) * 100).toFixed(1)}%` : "0%"; })(), color: "#dc2626" },
                  { label: "Active Clients", value: cls.filter(c => c.status === "Active").length, color: "#059669" },
                  { label: "Waitlist", value: wlCnt, color: "#d97706" },
                ].map(c => (
                  <div key={c.label} style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: 16, textAlign: "center" }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: c.color }}>{c.value}</div>
                    <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>{c.label}</div>
                  </div>
                ))}
              </div>
              {/* Caseload table */}
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Counselor Caseloads</h3>
              <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead><tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                    {["Counselor", "CIP", "SROP", "Total", "Intakes This Week"].map(h => (
                      <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, fontSize: 11, textTransform: "uppercase", color: "#475569" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>{cos.map(co => {
                    const coClients = cls.filter(c => c.counselor_id === co.id && c.status === "Active");
                    const cip = coClients.filter(c => c.program === "CIP").length;
                    const srop = coClients.filter(c => c.program === "SROP").length;
                    const used = appts.filter(a => a.counselor_id === co.id && a.type === "Intake/Assessment" && a.status !== "Cancelled").length;
                    const cap = getIntakeCap(co, sd);
                    return <tr key={co.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "8px 12px", fontWeight: 600 }}><span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: co.color }} />{co.name}</span></td>
                      <td style={{ padding: "8px 12px" }}>{cip}</td>
                      <td style={{ padding: "8px 12px" }}>{srop}</td>
                      <td style={{ padding: "8px 12px", fontWeight: 700 }}>{cip + srop}</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ background: used >= cap ? "#fee2e2" : "#dcfce7", color: used >= cap ? "#991b1b" : "#166534", padding: "2px 8px", borderRadius: 4, fontWeight: 700, fontSize: 11 }}>{used}/{cap}</span></td>
                    </tr>;
                  })}</tbody>
                </table>
              </div>
            </div>
          )}
          {reportSubTab === "summary" && (
            /* ── WEEKLY SUMMARY ── */
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <button onClick={() => { const d = new Date((summaryWeek || weekKey(monday)) + "T12:00:00"); d.setDate(d.getDate() - 7); setSummaryWeek(d.toISOString().split("T")[0]); }} style={{ ...bp, padding: "6px 12px", fontSize: 14, fontWeight: 700 }}>←</button>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                  Week of {new Date((summaryWeek || weekKey(monday)) + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
                <button onClick={() => { const d = new Date((summaryWeek || weekKey(monday)) + "T12:00:00"); d.setDate(d.getDate() + 7); setSummaryWeek(d.toISOString().split("T")[0]); }} style={{ ...bp, padding: "6px 12px", fontSize: 14, fontWeight: 700 }}>→</button>
                <button onClick={() => setSummaryWeek(weekKey(monday))} style={{ ...bp, fontSize: 12, color: "#64748b" }}>This Week</button>
              </div>

              {summaryLoading ? (
                <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", fontSize: 14 }}>Loading summary...</div>
              ) : weeklySummary.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", fontSize: 14 }}>No appointment data for this week.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {weeklySummary.map(s => (
                    <div key={s.counselor_id} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                      <div style={{ padding: "12px 16px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontWeight: 700, fontSize: 14, color: "#0f172a" }}>{s.counselor_name}</div>
                      <div style={{ padding: 16 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead><tr style={{ borderBottom: "2px solid #f1f5f9" }}>
                            {["Type", "Scheduled", "Attended", "No-Show"].map(h => (
                              <th key={h} style={{ textAlign: h === "Type" ? "left" : "center", padding: "6px 10px", fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                              <td style={{ padding: "8px 10px", fontWeight: 600 }}>Individual</td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>{s.individual.scheduled_hours}h</td>
                              <td style={{ padding: "8px 10px", textAlign: "center", color: "#059669", fontWeight: 600 }}>{s.individual.attended_hours}h</td>
                              <td style={{ padding: "8px 10px", textAlign: "center", color: s.individual.no_show_hours > 0 ? "#dc2626" : "#94a3b8", fontWeight: s.individual.no_show_hours > 0 ? 600 : 400 }}>{s.individual.no_show_hours}h</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                              <td style={{ padding: "8px 10px", fontWeight: 600 }}>Intake/Assessment</td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>{s.intake.scheduled_hours}h</td>
                              <td style={{ padding: "8px 10px", textAlign: "center", color: "#059669", fontWeight: 600 }}>{s.intake.attended_hours}h</td>
                              <td style={{ padding: "8px 10px", textAlign: "center", color: s.intake.no_show_hours > 0 ? "#dc2626" : "#94a3b8", fontWeight: s.intake.no_show_hours > 0 ? 600 : 400 }}>{s.intake.no_show_hours}h</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                              <td style={{ padding: "8px 10px", fontWeight: 600 }}>Group</td>
                              <td colSpan={3} style={{ padding: "8px 10px", textAlign: "center", fontWeight: 600 }}>{s.group.total_hours}h</td>
                            </tr>
                            <tr>
                              <td style={{ padding: "8px 10px", fontWeight: 600 }}>DRI</td>
                              <td colSpan={3} style={{ padding: "8px 10px", textAlign: "center", fontWeight: 600 }}>{s.dri.total_hours}h</td>
                            </tr>
                          </tbody>
                        </table>
                        <div style={{ display: "flex", gap: 16, marginTop: 12, padding: "10px 0", borderTop: "2px solid #e2e8f0" }}>
                          <div style={{ fontSize: 12, color: "#64748b" }}>
                            <span style={{ fontWeight: 700 }}>Total Scheduled:</span>{" "}
                            {(s.individual.scheduled_hours + s.intake.scheduled_hours + s.group.total_hours + s.dri.total_hours).toFixed(1)}h
                          </div>
                          <div style={{ fontSize: 12, color: "#059669" }}>
                            <span style={{ fontWeight: 700 }}>Total Attended:</span>{" "}
                            {(s.individual.attended_hours + s.intake.attended_hours + s.group.total_hours + s.dri.total_hours).toFixed(1)}h
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {reportSubTab === "available-intakes" && (
            /* ── AVAILABLE INTAKES ── */
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                <div style={{ fontSize: 13, color: "#475569" }}>
                  Click any row to schedule an intake at that slot. Slots reflect each counselor's work hours, current intake cap usage, and existing appointments.
                </div>
                <div style={{ flex: 1 }} />
                <select value={availIntakesFilterCo} onChange={e => setAvailIntakesFilterCo(e.target.value)} style={{ ...inp, width: "auto" }}>
                  <option value="all">All Counselors</option>
                  {cos.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button onClick={fetchAvailableIntakes} style={bp} disabled={availIntakesLoading}>{availIntakesLoading ? "Refreshing…" : "Refresh"}</button>
              </div>
              {availIntakesLoading && availIntakes === null ? (
                <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", fontSize: 14 }}>Loading available intakes…</div>
              ) : !availIntakes || availIntakes.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", fontSize: 14 }}>No available intake slots found.</div>
              ) : (() => {
                // Apply counselor filter then sort
                const filtered = availIntakes.filter(r => availIntakesFilterCo === "all" || r.counselor_id === Number(availIntakesFilterCo));
                const dir = availIntakesSort.dir === "desc" ? -1 : 1;
                const sortKey = availIntakesSort.key;
                const sorted = [...filtered].sort((a, b) => {
                  if (sortKey === "date") {
                    const c = a.date.localeCompare(b.date);
                    if (c !== 0) return c * dir;
                    const ta = a.slot.start_hour * 60 + (a.slot.start_min || 0);
                    const tb = b.slot.start_hour * 60 + (b.slot.start_min || 0);
                    return (ta - tb) * dir;
                  }
                  if (sortKey === "counselor") return a.counselor_name.localeCompare(b.counselor_name) * dir;
                  if (sortKey === "time") {
                    const ta = a.slot.start_hour * 60 + (a.slot.start_min || 0);
                    const tb = b.slot.start_hour * 60 + (b.slot.start_min || 0);
                    return (ta - tb) * dir;
                  }
                  if (sortKey === "load") {
                    const la = a.intake_cap - a.intake_used;
                    const lb = b.intake_cap - b.intake_used;
                    return (la - lb) * dir;
                  }
                  return 0;
                });
                const handleSort = (key) => {
                  setAvailIntakesSort(prev => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));
                };
                const sortIndicator = (key) => availIntakesSort.key === key ? (availIntakesSort.dir === "asc" ? " ▲" : " ▼") : "";
                return (
                  <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", overflow: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                      <thead><tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                        {[
                          { k: "date", l: "Date" },
                          { k: "time", l: "Time" },
                          { k: "counselor", l: "Counselor" },
                          { k: "load", l: "Intakes Remaining" },
                          { k: null, l: "Group Times" },
                        ].map(h => (
                          <th key={h.l} onClick={() => h.k && handleSort(h.k)}
                            style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, fontSize: 10.5, textTransform: "uppercase", color: "#475569", cursor: h.k ? "pointer" : "default", userSelect: "none" }}>
                            {h.l}{h.k && sortIndicator(h.k)}
                          </th>
                        ))}
                      </tr></thead>
                      <tbody>{sorted.map((r, idx) => {
                        const remaining = r.intake_cap - r.intake_used;
                        return (
                          <tr key={idx} onClick={() => openIntake({ counselor_id: r.counselor_id, counselor_name: r.counselor_name, date: r.date, slot: r.slot, intake_used: r.intake_used, intake_cap: r.intake_cap })}
                            style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#fafaff"}
                            onMouseLeave={e => e.currentTarget.style.background = ""}>
                            <td style={{ padding: "8px 12px", fontWeight: 600 }}>{new Date(r.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</td>
                            <td style={{ padding: "8px 12px" }}>{fm(r.slot.start_hour, r.slot.start_min)} – {fm(r.slot.end_hour, r.slot.end_min)}</td>
                            <td style={{ padding: "8px 12px" }}><span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: r.color || "#6366f1" }} />{r.counselor_name}</span></td>
                            <td style={{ padding: "8px 12px" }}>
                              <span style={{ background: remaining === 0 ? "#fee2e2" : "#dcfce7", color: remaining === 0 ? "#991b1b" : "#166534", padding: "2px 8px", borderRadius: 4, fontWeight: 700, fontSize: 11 }}>
                                {remaining} of {r.intake_cap}
                              </span>
                            </td>
                            <td style={{ padding: "8px 12px", fontSize: 11, color: "#64748b" }}>
                              {r.groups.length === 0 ? "—" : r.groups.map(g => `${g.name || "Group"} (${g.day} ${fm(g.start_hour, g.start_min || 0)})`).join(" · ")}
                            </td>
                          </tr>
                        );
                      })}</tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════ */}
      {/*  ADMIN TAB                               */}
      {/* ════════════════════════════════════════ */}
      {nav === "admin" && (() => {
        return <div>
          <h2 style={{ fontSize: 17, fontWeight: 800, marginBottom: 12 }}>Admin</h2>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {[...(isClinDir ? ["counselors"] : []), "intakes", "reminders", "topics", ...(isOwner ? ["users", "settings"] : [])].map(t => (
              <button key={t} onClick={() => setAdTab(t)} style={{ padding: "6px 14px", borderRadius: 7, border: adTab === t ? "2px solid #6366f1" : "1px solid #e2e8f0", background: adTab === t ? "#f0f0ff" : "#fff", color: adTab === t ? "#6366f1" : "#475569", fontWeight: 600, fontSize: 12, cursor: "pointer", textTransform: "capitalize" }}>{t}</button>
            ))}
          </div>

          {/* ── COUNSELORS ── */}
          {adTab === "counselors" && <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 20 }}>
            {cos.map(c => <div key={c.id} style={{ border: "1px solid #f1f5f9", borderRadius: 8, padding: 12, marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: c.color + "22", color: c.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>{c.init || c.name?.charAt(0)}</div>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{c.name}</span>
                </div>
                <button onClick={() => setShowEditCo({ ...c })} style={{ ...bp, padding: "4px 10px", fontSize: 11 }}>Edit</button>
              </div>
              <div style={{ fontSize: 11, color: "#64748b" }}>
                {c.g1?.name && <span>Groups: {c.g1.name} ({c.g1.day} {fm(c.g1.s, 0)}–{fm(c.g1.e, 0)}) · {c.g2?.name} ({c.g2?.day} {fm(c.g2?.s, 0)}–{fm(c.g2?.e, 0)})</span>}
              </div>
              <div style={{ fontSize: 11, color: "#64748b" }}>Topics: {c.topics?.length || 0} saved · Default intake cap: {c.default_intake_cap ?? 3}</div>
              {/* Per-counselor reminder templates */}
              <div style={{ marginTop: 8 }}>
                {["individual", "intake", "group"].map(t => <div key={t} style={{ marginBottom: 4 }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "capitalize" }}>{t} reminder:</label>
                  <textarea value={c[`reminder_content_${t}`] || ""} onChange={e => setCos(p => p.map(x => x.id === c.id ? { ...x, [`reminder_content_${t}`]: e.target.value } : x))} onBlur={async e => { await api.updateCounselor(c.id, { ...c, [`reminder_content_${t}`]: e.target.value }); st("Saved"); }} style={{ ...inp, minHeight: 32, resize: "vertical", fontSize: 10.5 }} placeholder="blank = program default" />
                </div>)}
              </div>
            </div>)}
          </div>}

          {/* ── INTAKES ── */}
          {adTab === "intakes" && (() => {
            const weeks = Array.from({ length: 8 }, (_, i) => {
              const m = getMon(today); m.setDate(m.getDate() + (capsWo * 8 + i) * 7); return m;
            });
            const wkKeys = weeks.map(w => weekKey(w));
            return <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 20 }}>
              <h3 style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 700 }}>Weekly Intake Caps</h3>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <button onClick={() => setCapsWo(o => o - 1)} style={{ ...bp, padding: "4px 10px" }}>← Prev 8</button>
                <button onClick={() => setCapsWo(0)} style={{ ...bp, fontSize: 11 }}>Current</button>
                <button onClick={() => setCapsWo(o => o + 1)} style={{ ...bp, padding: "4px 10px" }}>Next 8 →</button>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead><tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                    <th style={{ padding: "6px 8px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "#64748b", minWidth: 120 }}>Counselor</th>
                    <th style={{ padding: "6px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "#64748b", minWidth: 50 }}>Default</th>
                    {wkKeys.map((wk, i) => <th key={wk} style={{ padding: "6px 4px", textAlign: "center", fontWeight: 600, fontSize: 9.5, color: "#64748b", minWidth: 60 }}>{fmtDateShort(wk)}</th>)}
                  </tr></thead>
                  <tbody>{cos.map(c => <tr key={c.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "6px 8px", fontWeight: 600, fontSize: 12 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.color }} />{c.name}
                      </span>
                    </td>
                    <td style={{ padding: "4px", textAlign: "center" }}>
                      <select value={c.default_intake_cap ?? 3} onChange={async e => { await api.updateCounselor(c.id, { ...c, default_intake_cap: Number(e.target.value) }); reloadCos(); st("Updated"); }} style={{ ...sl, width: 44, fontSize: 11, padding: "2px 4px", textAlign: "center" }}>
                        {[0, 1, 2, 3, 4, 5, 6].map(n => <option key={n}>{n}</option>)}
                      </select>
                    </td>
                    {wkKeys.map(wk => {
                      const ov = c.intakeCaps?.[wk];
                      return <td key={wk} style={{ padding: "4px", textAlign: "center" }}>
                        <select value={ov ?? ""} onChange={async e => { const v = e.target.value; await api.setIntakeCap(c.id, wk, v === "" ? null : Number(v)); reloadCos(); st("Updated"); }} style={{ ...sl, width: 44, fontSize: 11, padding: "2px 4px", textAlign: "center", background: ov != null ? "#fef3c7" : "#fff" }}>
                          <option value="">—</option>
                          {[0, 1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </td>;
                    })}
                  </tr>)}</tbody>
                </table>
              </div>
              <p style={{ fontSize: 10, color: "#94a3b8", marginTop: 8 }}>Yellow cells have per-week overrides. "—" uses the default. Showing 8 weeks at a time.</p>
            </div>;
          })()}

          {/* ── REMINDERS ── */}
          {adTab === "reminders" && <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 20 }}>
            <h3 style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 700 }}>Program Reminders</h3>
            <p style={{ fontSize: 11, color: "#64748b", margin: "0 0 16px" }}>Variables available in templates: {"{client}, {counselor}, {date}, {time}, {topic}"}</p>

            {remCfg.map(cfg => {
              const programIntakeReminders = intakeReminders.filter(r => r.program === cfg.program).sort((a, b) => b.days_before - a.days_before);
              return <div key={cfg.program} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, marginBottom: 16, background: "#fafbfc" }}>
                <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 10, color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 11, background: cfg.program === "CIP" ? "#dcfce7" : "#dbeafe", color: cfg.program === "CIP" ? "#166534" : "#1e40af" }}>{cfg.program}</span>
                </div>

                {/* ── Master enable toggles ── */}
                <div style={{ display: "flex", gap: 14, marginBottom: 14, flexWrap: "wrap", alignItems: "center", paddingBottom: 10, borderBottom: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Channels:</span>
                  <Chk c={!!cfg.email_enabled} onChange={async () => { const v = !cfg.email_enabled; await api.updateReminderConfig(cfg.program, { email_enabled: v }); setRemCfg(p => p.map(x => x.program === cfg.program ? { ...x, email_enabled: v } : x)); st("Updated"); }} label="Email" />
                  <Chk c={!!cfg.text_enabled} onChange={async () => { const v = !cfg.text_enabled; await api.updateReminderConfig(cfg.program, { text_enabled: v }); setRemCfg(p => p.map(x => x.program === cfg.program ? { ...x, text_enabled: v } : x)); st("Updated"); }} label="Text" />
                </div>

                {/* ── Standard appointment reminder ── */}
                <div style={{ marginBottom: 14 }}>
                  <h4 style={{ fontSize: 12, fontWeight: 700, margin: "0 0 6px", color: "#475569", textTransform: "uppercase", letterSpacing: ".5px" }}>Standard Appointment Reminder</h4>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: 11, color: "#64748b" }}>Send</span>
                    <select value={cfg.days_before || 1} onChange={async e => { const v = Number(e.target.value); await api.updateReminderConfig(cfg.program, { days_before: v }); setRemCfg(p => p.map(x => x.program === cfg.program ? { ...x, days_before: v } : x)); st("Updated"); }} style={{ ...sl, width: 50, fontSize: 11 }}>
                      {[1, 2, 3, 5, 7].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span style={{ fontSize: 11, color: "#64748b" }}>day(s) before each Individual / Group appointment</span>
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    <label style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Email content</label>
                    <textarea value={cfg.email_content || ""} onChange={e => setRemCfg(p => p.map(x => x.program === cfg.program ? { ...x, email_content: e.target.value } : x))} onBlur={async e => { await api.updateReminderConfig(cfg.program, { email_content: e.target.value }); st("Saved"); }} style={{ ...inp, minHeight: 40, resize: "vertical", fontSize: 11 }} placeholder="Hi {client}, this is a reminder of your appointment on {date} at {time}." />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>SMS content</label>
                    <textarea value={cfg.sms_content || ""} onChange={e => setRemCfg(p => p.map(x => x.program === cfg.program ? { ...x, sms_content: e.target.value } : x))} onBlur={async e => { await api.updateReminderConfig(cfg.program, { sms_content: e.target.value }); st("Saved"); }} style={{ ...inp, minHeight: 36, resize: "vertical", fontSize: 11 }} placeholder="Reminder: {date} at {time} with {counselor}." />
                  </div>
                </div>

                {/* ── Intake reminder sequence (NEW) ── */}
                <div style={{ marginBottom: 14, paddingTop: 10, borderTop: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <h4 style={{ fontSize: 12, fontWeight: 700, margin: 0, color: "#475569", textTransform: "uppercase", letterSpacing: ".5px" }}>Intake Reminder Sequence</h4>
                    <button onClick={async () => {
                      try {
                        await api.addIntakeReminder(cfg.program, { days_before: 7, email_enabled: true, text_enabled: true, email_content: "", sms_content: "" });
                        await reloadIntakeReminders();
                        st("Reminder added");
                      } catch (e) { st("Add failed: " + (e.message || "error"), "warning"); }
                    }} style={{ ...bs, padding: "4px 10px", display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}><PlI /> Add Reminder</button>
                  </div>
                  <p style={{ fontSize: 10.5, color: "#94a3b8", margin: "0 0 8px" }}>Add as many pre-intake reminders as you want, each with its own days-before and message. Sent in order from longest lead time to shortest.</p>

                  {programIntakeReminders.length === 0 && (
                    <div style={{ padding: 10, background: "#fff", borderRadius: 6, border: "1px dashed #cbd5e1", textAlign: "center", fontSize: 11, color: "#94a3b8" }}>No intake reminders yet for {cfg.program}. Click "Add Reminder" to create one.</div>
                  )}

                  {programIntakeReminders.map(rem => (
                    <div key={rem.id} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, marginBottom: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Send</span>
                        <input type="number" min="1" max="60" value={rem.days_before} onChange={e => {
                          const v = Math.max(1, Math.min(60, Number(e.target.value) || 1));
                          setIntakeReminders(p => p.map(x => x.id === rem.id ? { ...x, days_before: v } : x));
                        }} onBlur={async e => {
                          const v = Math.max(1, Math.min(60, Number(e.target.value) || 1));
                          await api.updateIntakeReminder(rem.id, { days_before: v }); st("Saved");
                        }} style={{ ...inp, width: 56, fontSize: 12, padding: "4px 6px", textAlign: "center" }} />
                        <span style={{ fontSize: 11, color: "#64748b" }}>day(s) before intake</span>
                        <div style={{ flex: 1 }} />
                        <Chk c={!!rem.email_enabled} onChange={async () => {
                          const v = !rem.email_enabled;
                          await api.updateIntakeReminder(rem.id, { email_enabled: v });
                          setIntakeReminders(p => p.map(x => x.id === rem.id ? { ...x, email_enabled: v } : x));
                          st("Updated");
                        }} label="Email" />
                        <Chk c={!!rem.text_enabled} onChange={async () => {
                          const v = !rem.text_enabled;
                          await api.updateIntakeReminder(rem.id, { text_enabled: v });
                          setIntakeReminders(p => p.map(x => x.id === rem.id ? { ...x, text_enabled: v } : x));
                          st("Updated");
                        }} label="Text" />
                        <button onClick={async () => {
                          if (!window.confirm(`Delete this ${rem.days_before}-day-before reminder?`)) return;
                          try { await api.deleteIntakeReminder(rem.id); await reloadIntakeReminders(); st("Deleted"); }
                          catch (e) { st("Delete failed", "warning"); }
                        }} style={{ ...bd, padding: "3px 8px", fontSize: 10 }}>Remove</button>
                      </div>
                      <div style={{ marginBottom: 6 }}>
                        <label style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Email content</label>
                        <textarea value={rem.email_content || ""} onChange={e => setIntakeReminders(p => p.map(x => x.id === rem.id ? { ...x, email_content: e.target.value } : x))} onBlur={async e => { await api.updateIntakeReminder(rem.id, { email_content: e.target.value }); st("Saved"); }} style={{ ...inp, minHeight: 40, resize: "vertical", fontSize: 11 }} placeholder={`Hi {client}, your intake with {counselor} is in ${rem.days_before} day(s) on {date} at {time}.`} />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>SMS content</label>
                        <textarea value={rem.sms_content || ""} onChange={e => setIntakeReminders(p => p.map(x => x.id === rem.id ? { ...x, sms_content: e.target.value } : x))} onBlur={async e => { await api.updateIntakeReminder(rem.id, { sms_content: e.target.value }); st("Saved"); }} style={{ ...inp, minHeight: 36, resize: "vertical", fontSize: 11 }} placeholder={`Intake in ${rem.days_before} day(s): {date} at {time} with {counselor}.`} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* ── Appointment confirmation (NEW) ── */}
                <div style={{ paddingTop: 10, borderTop: "1px solid #e2e8f0" }}>
                  <h4 style={{ fontSize: 12, fontWeight: 700, margin: "0 0 6px", color: "#475569", textTransform: "uppercase", letterSpacing: ".5px" }}>Appointment Confirmation</h4>
                  <p style={{ fontSize: 10.5, color: "#94a3b8", margin: "0 0 8px" }}>Sent immediately when a scheduler checks "Send confirmation now" while booking. Respects the client's contact preference.</p>
                  <div style={{ display: "flex", gap: 14, marginBottom: 8, flexWrap: "wrap" }}>
                    <Chk c={cfg.confirmation_email_enabled !== false} onChange={async () => {
                      const v = !(cfg.confirmation_email_enabled !== false);
                      await api.updateReminderConfig(cfg.program, { confirmation_email_enabled: v });
                      setRemCfg(p => p.map(x => x.program === cfg.program ? { ...x, confirmation_email_enabled: v } : x));
                      st("Updated");
                    }} label="Email enabled" />
                    <Chk c={cfg.confirmation_text_enabled !== false} onChange={async () => {
                      const v = !(cfg.confirmation_text_enabled !== false);
                      await api.updateReminderConfig(cfg.program, { confirmation_text_enabled: v });
                      setRemCfg(p => p.map(x => x.program === cfg.program ? { ...x, confirmation_text_enabled: v } : x));
                      st("Updated");
                    }} label="Text enabled" />
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    <label style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Email content</label>
                    <textarea value={cfg.confirmation_email_content || ""} onChange={e => setRemCfg(p => p.map(x => x.program === cfg.program ? { ...x, confirmation_email_content: e.target.value } : x))} onBlur={async e => { await api.updateReminderConfig(cfg.program, { confirmation_email_content: e.target.value }); st("Saved"); }} style={{ ...inp, minHeight: 50, resize: "vertical", fontSize: 11 }} placeholder="Hi {client}, this confirms your appointment with {counselor} on {date} at {time}." />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>SMS content</label>
                    <textarea value={cfg.confirmation_sms_content || ""} onChange={e => setRemCfg(p => p.map(x => x.program === cfg.program ? { ...x, confirmation_sms_content: e.target.value } : x))} onBlur={async e => { await api.updateReminderConfig(cfg.program, { confirmation_sms_content: e.target.value }); st("Saved"); }} style={{ ...inp, minHeight: 36, resize: "vertical", fontSize: 11 }} placeholder="Confirmed: {date} at {time} with {counselor}." />
                  </div>
                </div>
              </div>;
            })}

            {remCfg.length === 0 && <p style={{ color: "#94a3b8", fontSize: 12 }}>No reminder configuration found. Ensure CIP and SROP entries exist in the reminder_config table.</p>}
          </div>}

          {/* ── TOPICS ── */}
          {adTab === "topics" && <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 20 }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 700 }}>Global Group Topics</h3>
            <p style={{ fontSize: 11, color: "#64748b", margin: "0 0 8px" }}>These topics are available to all counselors. Counselors can also have their own additional topics.</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
              {gTopics.map((t, j) => <span key={j} style={{ background: "#e0e7ff", padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, color: "#4338ca", display: "flex", alignItems: "center", gap: 4 }}>
                {t}
                <button onClick={async () => { const nt = gTopics.filter((_, k) => k !== j); setGTopics(nt); await api.updateGlobalTopics(nt); st("Removed"); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#6366f1", fontSize: 14, padding: 0 }}>×</button>
              </span>)}
              {gTopics.length === 0 && <span style={{ color: "#94a3b8", fontSize: 12 }}>No global topics yet</span>}
            </div>
            <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
              <input id="gtp" style={{ ...inp, flex: 1 }} placeholder="Add global topic..." onKeyDown={async e => { if (e.key === "Enter" && e.target.value.trim()) { const nt = [...gTopics, e.target.value.trim()]; setGTopics(nt); await api.updateGlobalTopics(nt); e.target.value = ""; st("Added"); } }} />
              <button onClick={async () => { const el = document.getElementById("gtp"); if (el.value.trim()) { const nt = [...gTopics, el.value.trim()]; setGTopics(nt); await api.updateGlobalTopics(nt); el.value = ""; st("Added"); } }} style={{ ...bs, padding: "6px 12px" }}>Add</button>
            </div>

            <h3 style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 700 }}>Additional Topics by Counselor</h3>
            {cos.map(c => <div key={c.id} style={{ border: "1px solid #f1f5f9", borderRadius: 6, padding: 10, marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.color }} />{c.name}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 6 }}>
                {(c.topics || []).map((t, j) => <span key={j} style={{ background: "#f1f5f9", padding: "2px 7px", borderRadius: 4, fontSize: 10.5, display: "flex", alignItems: "center", gap: 3 }}>
                  {t}
                  <button onClick={async () => { const nt = (c.topics || []).filter((_, k) => k !== j); await api.updateCounselor(c.id, { ...c, topics: nt }); reloadCos(); st("Removed"); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 12, padding: 0 }}>×</button>
                </span>)}
              </div>
              <div style={{ display: "flex", gap: 3 }}>
                <input id={`ct-${c.id}`} style={{ ...inp, flex: 1, fontSize: 11 }} placeholder="Add topic..." onKeyDown={async e => { if (e.key === "Enter" && e.target.value.trim()) { const nt = [...(c.topics || []), e.target.value.trim()]; await api.updateCounselor(c.id, { ...c, topics: nt }); reloadCos(); e.target.value = ""; st("Added"); } }} />
                <button onClick={async () => { const el = document.getElementById(`ct-${c.id}`); if (el.value.trim()) { const nt = [...(c.topics || []), el.value.trim()]; await api.updateCounselor(c.id, { ...c, topics: nt }); reloadCos(); el.value = ""; st("Added"); } }} style={{ ...bs, padding: "4px 10px", fontSize: 10.5 }}>Add</button>
              </div>
            </div>)}
          </div>}

          {/* ── USERS (owner only) ── */}
          {adTab === "users" && isOwner && <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>User Management</h3>
              <button onClick={() => setShowEditUser({ username: "", password: "", role: "counselor", counselor_id: "", name: "", email: "" })} style={{ ...bs, padding: "6px 12px", display: "flex", alignItems: "center", gap: 4 }}><PlI /> Add User</button>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr style={{ borderBottom: "2px solid #f1f5f9", background: "#fafbfc" }}>
                {["Name", "Username", "Role", "Linked Counselor", "Active", ""].map((h, j) => <th key={j} style={{ padding: "6px 8px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>{h}</th>)}
              </tr></thead>
              <tbody>{users.map(u => {
                const co = cos.find(c => c.id === u.counselor_id);
                const rl = { counselor: "Counselor", admin: "Admin", ops_director: "Ops Dir", clinical_director: "Clinical Dir", owner: "Owner" }[u.role] || u.role;
                return <tr key={u.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "6px 8px", fontWeight: 600 }}>{u.name}</td>
                  <td style={{ padding: "6px 8px", color: "#64748b" }}>{u.username}</td>
                  <td style={{ padding: "6px 8px" }}><span style={{ background: u.role === "owner" ? "#fee2e2" : u.role === "clinical_director" ? "#fce7f3" : u.role === "ops_director" ? "#fef3c7" : u.role === "admin" ? "#e0e7ff" : "#f1f5f9", color: u.role === "owner" ? "#991b1b" : u.role === "clinical_director" ? "#9d174d" : u.role === "ops_director" ? "#92400e" : u.role === "admin" ? "#4338ca" : "#475569", padding: "2px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600 }}>{rl}</span></td>
                  <td style={{ padding: "6px 8px" }}>{co ? <span style={{ display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: co.color }} />{co.name}</span> : "—"}</td>
                  <td style={{ padding: "6px 8px" }}><span style={{ color: u.is_active ? "#059669" : "#dc2626", fontWeight: 600, fontSize: 11 }}>{u.is_active ? "Yes" : "No"}</span></td>
                  <td style={{ padding: "6px 8px" }}><button onClick={() => setShowEditUser({ ...u, password: "" })} style={{ ...bp, padding: "3px 8px", fontSize: 10 }}>Edit</button></td>
                </tr>;
              })}</tbody>
            </table>
            {users.length === 0 && <p style={{ color: "#94a3b8", fontSize: 12, textAlign: "center", padding: 20 }}>No users found. Add a user to get started.</p>}
          </div>}

          {/* ── SETTINGS (owner only) ── */}
          {adTab === "settings" && isOwner && <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 20 }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>Email (SMTP) Settings</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              <Fl l="SMTP Host"><input value={settings.smtp_host || ""} onChange={e => setSettings(s => ({ ...s, smtp_host: e.target.value }))} style={inp} placeholder="smtp.ionos.com" /></Fl>
              <Fl l="SMTP Port"><input value={settings.smtp_port || "587"} onChange={e => setSettings(s => ({ ...s, smtp_port: e.target.value }))} style={inp} placeholder="587" /></Fl>
              <Fl l="SMTP Username"><input value={settings.smtp_user || ""} onChange={e => setSettings(s => ({ ...s, smtp_user: e.target.value }))} style={inp} placeholder="email@yourdomain.com" /></Fl>
              <Fl l="SMTP Password"><input type="password" value={settings.smtp_pass || ""} onChange={e => setSettings(s => ({ ...s, smtp_pass: e.target.value }))} style={inp} /></Fl>
              <Fl l="From Address"><input value={settings.smtp_from || ""} onChange={e => setSettings(s => ({ ...s, smtp_from: e.target.value }))} style={inp} placeholder="noreply@yourdomain.com" /></Fl>
              <Fl l="Enabled"><select value={settings.smtp_enabled ? "true" : "false"} onChange={e => setSettings(s => ({ ...s, smtp_enabled: e.target.value === "true" }))} style={sl}><option value="false">Disabled</option><option value="true">Enabled</option></select></Fl>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "flex-end", marginBottom: 16 }}>
              <button onClick={async () => { try { await api.saveSettings(settings); st("SMTP settings saved"); } catch (e) { st("Error: " + e.message, "warning"); } }} style={bp}>Save SMTP</button>
              <div style={{ flex: 1 }}><Fl l="Test Email To"><input value={testEmail} onChange={e => setTestEmail(e.target.value)} style={inp} placeholder="test@example.com" /></Fl></div>
              <button onClick={async () => { if (!testEmail) { st("Enter email", "warning"); return; } try { const r = await api.testEmail(testEmail); st(r.ok ? "Test email sent!" : "Failed: " + (r.error || "unknown"), "info"); } catch (e) { st("Error: " + e.message, "warning"); } }} style={{ ...bs, marginBottom: 10 }}>Send Test</button>
            </div>

            <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>TextMagic (SMS) Settings</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              <Fl l="TextMagic Username"><input value={settings.textmagic_user || ""} onChange={e => setSettings(s => ({ ...s, textmagic_user: e.target.value }))} style={inp} /></Fl>
              <Fl l="TextMagic API Key"><input type="password" value={settings.textmagic_key || ""} onChange={e => setSettings(s => ({ ...s, textmagic_key: e.target.value }))} style={inp} /></Fl>
              <Fl l="Enabled"><select value={settings.textmagic_enabled ? "true" : "false"} onChange={e => setSettings(s => ({ ...s, textmagic_enabled: e.target.value === "true" }))} style={sl}><option value="false">Disabled</option><option value="true">Enabled</option></select></Fl>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
              <button onClick={async () => { try { await api.saveSettings(settings); st("SMS settings saved"); } catch (e) { st("Error: " + e.message, "warning"); } }} style={bp}>Save SMS</button>
              <div style={{ flex: 1 }}><Fl l="Test SMS To"><input value={testPhone} onChange={e => setTestPhone(e.target.value)} style={inp} placeholder="+18165550123" /></Fl></div>
              <button onClick={async () => { if (!testPhone) { st("Enter phone", "warning"); return; } try { const r = await api.testSMS(testPhone); st(r.ok ? "Test SMS sent!" : "Failed: " + (r.error || "unknown"), "info"); } catch (e) { st("Error: " + e.message, "warning"); } }} style={{ ...bs, marginBottom: 10 }}>Send Test</button>
            </div>
          </div>}

        </div>;
      })()}

      </div>

      {/* ════════════════════════════════════════ */}
      {/*  APPOINTMENT DETAIL MODAL               */}
      {/* ════════════════════════════════════════ */}
      {sel && (() => {
        const a = sel;
        const co = cos.find(c => c.id === a.counselor_id);
        const cl = a.client_id ? cls.find(c => c.id === a.client_id) : null;
        const t = getTC(a, cls);
        const sb = SB[a.status] || SB.Scheduled;
        return <Modal title="Appointment Details" onClose={() => setSel(null)}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <span style={{ padding: "3px 10px", borderRadius: 5, fontSize: 11, fontWeight: 700, background: t.bg, color: t.text, border: `1px solid ${t.border}` }}>{a.type}</span>
            {a.program && <span style={{ padding: "3px 10px", borderRadius: 5, fontSize: 11, fontWeight: 700, background: a.program === "CIP" ? "#dcfce7" : "#dbeafe", color: a.program === "CIP" ? "#166534" : "#1e40af" }}>{a.program}</span>}
            <span style={{ padding: "3px 10px", borderRadius: 5, fontSize: 11, fontWeight: 700, background: sb.bg, color: sb.text }}>{a.status}</span>
          </div>
          <div style={{ fontSize: 13, color: "#334155", lineHeight: 2 }}>
            <div><strong>Date:</strong> {fmtDateShort(a.date)}</div>
            <div><strong>Time:</strong> {fm(a.start_hour, a.start_min)} — {fm(a.end_hour, a.end_min)}</div>
            <div><strong>Counselor:</strong> {co?.name || "—"}</div>
            {cl && <div><strong>Client:</strong> {cl.last_name}, {cl.first_name} · {cl.phone} · <span style={{ userSelect: "text" }}>{cl.email}</span></div>}
            {a.computed_session_num && <div><strong>Session:</strong> #{a.computed_session_num}</div>}
            {a.type === "Group" && <div><strong>Group:</strong> {a.group_name} {a.group_topic ? `· ${a.group_topic}` : ""}</div>}
            {a.type === "DRI" && <div><strong>DRI:</strong> {a.dri_label || "—"}</div>}
            {a.notes && <div><strong>Notes:</strong> {a.notes}</div>}
            {a.recur_series_id && <div><strong>Recurring</strong> (series)</div>}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 16, flexWrap: "wrap" }}>
            <button onClick={() => markAttended(a)} style={a.attended ? bd : bpP}>{a.attended ? "Unmark Attended" : "Mark Attended"}</button>
            <button onClick={() => markNoShow(a)} style={{ ...bp, color: "#dc2626" }}>No-Show</button>
            <button onClick={() => { setNw({ ...a }); setSel(null); setShowNew(true); }} style={bp}>Edit</button>
            {cl && a.type !== "Blocked" && a.type !== "DRI" && a.type !== "Group" && (
              <button onClick={async () => {
                try {
                  const conf = await api.sendConfirmation(a.id);
                  if (conf?.sent_email || conf?.sent_sms) {
                    st(`Confirmation sent (${[conf.sent_email && "email", conf.sent_sms && "text"].filter(Boolean).join(" + ")})`, "info");
                  } else if (conf?.skipped_reason) {
                    st(`Not sent: ${conf.skipped_reason}`, "warning");
                  }
                } catch (e) { st("Send failed: " + (e.message || "error"), "warning"); }
              }} style={bp}>Send Confirmation</button>
            )}
            {a.recur_series_id ? (
              <button onClick={() => { setShowDelSeries(a); setSel(null); }} style={bd}>Delete...</button>
            ) : (
              <button onClick={() => delAppt(a.id)} style={bd}>Delete</button>
            )}
          </div>
        </Modal>;
      })()}

      {/* ════════════════════════════════════════ */}
      {/*  RECURRING DELETE MODAL                  */}
      {/* ════════════════════════════════════════ */}
      {showDelSeries && (
        <Modal title="Delete Recurring Appointment" onClose={() => setShowDelSeries(null)}>
          <p style={{ fontSize: 13, color: "#475569", marginBottom: 16 }}>This appointment is part of a recurring series. What would you like to delete?</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button onClick={() => { delAppt(showDelSeries.id); setShowDelSeries(null); }} style={bp}>This appointment only</button>
            <button onClick={() => { delAppt(showDelSeries.id, { series: "true", future_only: "true" }); setShowDelSeries(null); }} style={bp}>This and all future in series</button>
            <button onClick={() => { delAppt(showDelSeries.id, { series: "true" }); setShowDelSeries(null); }} style={bd}>Delete entire series</button>
          </div>
        </Modal>
      )}

      {/* ════════════════════════════════════════ */}
      {/*  NEW / EDIT APPOINTMENT MODAL            */}
      {/* ════════════════════════════════════════ */}
      {showNew && nw && (
        <Modal title={nw.id ? "Edit Appointment" : "New Appointment"} onClose={() => { setShowNew(false); setNw(null); }} width={520}>
          <Fl l="Type">
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {["Individual", "Intake/Assessment", "Group", "DRI", "Blocked"].map(t => (
                <button key={t} onClick={() => {
                  const dur = { Individual: 1, "Intake/Assessment": 1.5, Group: 4, DRI: 6, Blocked: 1 };
                  setNw(a => ({ ...a, type: t, end_hour: a.start_hour + Math.floor(dur[t]), end_min: (dur[t] % 1) * 60 }));
                }} style={{ padding: "4px 10px", borderRadius: 5, border: nw.type === t ? "2px solid #6366f1" : "1px solid #e2e8f0", background: nw.type === t ? "#f0f0ff" : "#fff", color: nw.type === t ? "#6366f1" : "#475569", fontWeight: 600, fontSize: 11, cursor: "pointer" }}>{t}</button>
              ))}
            </div>
          </Fl>
          {nw.type === "Blocked" ? (
            <Fl l="Reason">
              <select value={nw.blocked_reason || "Lunch"} onChange={e => setNw(a => ({ ...a, blocked_reason: e.target.value }))} style={inp}>
                {["Lunch", "Group Prep", "Paperwork", "Other"].map(r => <option key={r}>{r}</option>)}
              </select>
              {nw.blocked_reason === "Other" && <input placeholder="Describe..." value={nw.blocked_other || ""} onChange={e => setNw(a => ({ ...a, blocked_other: e.target.value }))} style={{ ...inp, marginTop: 6 }} />}
            </Fl>
          ) : nw.type === "DRI" ? (
            <Fl l="DRI Label"><input value={nw.dri_label || ""} onChange={e => setNw(a => ({ ...a, dri_label: e.target.value }))} placeholder="e.g. Documentation Review" style={inp} /></Fl>
          ) : nw.type === "Group" ? (
            <div>
              <Fl l="Group Name"><input value={nw.group_name || ""} onChange={e => setNw(a => ({ ...a, group_name: e.target.value }))} style={inp} /></Fl>
              <Fl l="Topic"><input value={nw.group_topic || ""} onChange={e => setNw(a => ({ ...a, group_topic: e.target.value }))} placeholder="e.g. Relapse Prevention" style={inp} /></Fl>
            </div>
          ) : (
            <div>
              {!ncts.includes(nw.type) && (
                <Fl l="Client">
                  <select value={nw.client_id || ""} onChange={e => setNw(a => ({ ...a, client_id: Number(e.target.value) || null }))} style={inp}>
                    <option value="">Select client...</option>
                    {(clMode === "caseload" ? caseloadCls : allActiveCls).map(c => <option key={c.id} value={c.id}>{c.last_name}, {c.first_name} ({c.program})</option>)}
                  </select>
                  <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                    <button onClick={() => setClMode("caseload")} style={{ fontSize: 10, fontWeight: 600, color: clMode === "caseload" ? "#6366f1" : "#94a3b8", background: "none", border: "none", cursor: "pointer" }}>This counselor</button>
                    <button onClick={() => setClMode("all")} style={{ fontSize: 10, fontWeight: 600, color: clMode === "all" ? "#6366f1" : "#94a3b8", background: "none", border: "none", cursor: "pointer" }}>All clients</button>
                  </div>
                </Fl>
              )}
              <Fl l="Program">
                <div style={{ display: "flex", gap: 4 }}>
                  {["CIP", "SROP"].map(p => <button key={p} onClick={() => setNw(a => ({ ...a, program: p }))} style={{ padding: "4px 12px", borderRadius: 5, border: nw.program === p ? "2px solid #6366f1" : "1px solid #e2e8f0", background: nw.program === p ? "#f0f0ff" : "#fff", fontWeight: 600, fontSize: 11, cursor: "pointer", color: nw.program === p ? "#6366f1" : "#475569" }}>{p}</button>)}
                </div>
              </Fl>
            </div>
          )}
          <Fl l="Counselor">
            <select value={nw.counselor_id || fco} onChange={e => setNw(a => ({ ...a, counselor_id: Number(e.target.value) }))} style={inp}>
              {cos.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Fl>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <Fl l="Date"><input type="date" value={nw.date} onChange={e => setNw(a => ({ ...a, date: e.target.value }))} style={inp} /></Fl>
            <Fl l="Start">
              <select value={`${nw.start_hour}:${nw.start_min || 0}`} onChange={e => { const [h, m] = e.target.value.split(":").map(Number); setNw(a => ({ ...a, start_hour: h, start_min: m })); }} style={inp}>
                {HOURS.flatMap(h => [0, 30].map(m => <option key={`${h}:${m}`} value={`${h}:${m}`}>{fm(h, m)}</option>))}
              </select>
            </Fl>
            <Fl l="End">
              <select value={`${nw.end_hour}:${nw.end_min || 0}`} onChange={e => { const [h, m] = e.target.value.split(":").map(Number); setNw(a => ({ ...a, end_hour: h, end_min: m })); }} style={inp}>
                {HOURS.flatMap(h => [0, 30].map(m => <option key={`${h}:${m}`} value={`${h}:${m}`}>{fm(h, m)}</option>))}
                <option value="23:0">{fm(23, 0)}</option>
              </select>
            </Fl>
          </div>
          {nw.type !== "Blocked" && (
            <Fl l="Notes"><textarea value={nw.notes || ""} onChange={e => setNw(a => ({ ...a, notes: e.target.value }))} rows={2} style={{ ...inp, resize: "vertical" }} /></Fl>
          )}
          {!nw.id && nw.client_id && nw.type !== "Blocked" && nw.type !== "DRI" && nw.type !== "Group" && (
            <div style={{ marginBottom: 10, padding: "8px 10px", background: "#f0f9ff", borderRadius: 6, border: "1px solid #bae6fd" }}>
              <Chk c={sendConfirmation} onChange={() => setSendConfirmation(v => !v)} label="Send appointment confirmation now (uses client's contact preference)" />
            </div>
          )}
          {!nw.id && (
            <div style={{ marginBottom: 10 }}>
              <Chk c={nw.recurring} onChange={() => setNw(a => ({ ...a, recurring: !a.recurring }))} label="Recurring" />
              {nw.recurring && (
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <select value={nw.recur_pattern || "weekly"} onChange={e => setNw(a => ({ ...a, recur_pattern: e.target.value }))} style={{ ...inp, width: "auto" }}>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Biweekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                  <input type="date" value={nw.recur_end_date || ""} onChange={e => setNw(a => ({ ...a, recur_end_date: e.target.value }))} style={{ ...inp, width: "auto" }} placeholder="End date" />
                </div>
              )}
            </div>
          )}
          <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
            <button onClick={() => {
              if (nw.type === "Blocked" && nw.blocked_reason === "Other" && !nw.blocked_other?.trim()) { st("Description required", "warning"); return; }
              const toSave = nw.type === "Blocked" && nw.blocked_reason === "Other" ? { ...nw, blocked_reason: nw.blocked_other } : nw;
              saveAppt(toSave);
            }} style={{ ...bpP, opacity: saving ? .6 : 1 }} disabled={saving}>{saving ? "Saving..." : nw.id ? "Save" : "Create"}</button>
            <button onClick={() => { setShowNew(false); setNw(null); }} style={bp}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* ════════════════════════════════════════ */}
      {/*  CLIENT DETAIL MODAL                     */}
      {/* ════════════════════════════════════════ */}
      {showCD && (() => {
        const c = showCD;
        const co = cos.find(x => x.id === c.counselor_id);
        const bal = parseFloat(c.total_owed || 0) - parseFloat(c.total_paid || 0);
        return <Modal title={`${c.last_name}, ${c.first_name}`} onClose={() => setShowCD(null)}>
          <div style={{ fontSize: 13, color: "#334155", lineHeight: 2 }}>
            <div><strong>Phone:</strong> {c.phone}</div>
            <div><strong>Email:</strong> <span style={{ userSelect: "text" }}>{c.email}</span></div>
            <div><strong>Program:</strong> {c.program}</div>
            <div><strong>Counselor:</strong> {co?.name || "—"}</div>
            <div><strong>Status:</strong> {c.status}</div>
            <div><strong>Balance:</strong> <span style={{ color: bal > 0 ? "#dc2626" : "#059669", fontWeight: 700 }}>${bal.toFixed(2)}</span> (Owed: ${parseFloat(c.total_owed || 0).toFixed(2)} / Paid: ${parseFloat(c.total_paid || 0).toFixed(2)})</div>
            <div><strong>Contact Preference:</strong> {c.contact_pref || "—"}</div>
            {c.completed_individual > 0 && <div><strong>Individual Sessions:</strong> {c.completed_individual} completed</div>}
            {c.completed_intake > 0 && <div><strong>Intake:</strong> Completed</div>}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
            <button onClick={() => { setShowCD(null); setShowAddCl(c); }} style={bpP}>Edit</button>
            <button onClick={() => { setShowCD(null); setNav("calendar"); openNew(0, 9); setNw(a => ({ ...a, client_id: c.id, program: c.program, counselor_id: c.counselor_id || fco })); }} style={bp}>Schedule</button>
          </div>
        </Modal>;
      })()}

      {/* ════════════════════════════════════════ */}
      {/*  ADD/EDIT CLIENT MODAL                   */}
      {/* ════════════════════════════════════════ */}
      {showAddCl && (
        <Modal title={showAddCl.id ? "Edit Client" : "Add Client"} onClose={() => setShowAddCl(null)}>
          <Fl l="First Name"><input value={showAddCl.first_name || ""} onChange={e => setShowAddCl(a => ({ ...a, first_name: e.target.value }))} style={inp} /></Fl>
          <Fl l="Last Name"><input value={showAddCl.last_name || ""} onChange={e => setShowAddCl(a => ({ ...a, last_name: e.target.value }))} style={inp} /></Fl>
          <Fl l="Phone"><input value={showAddCl.phone || ""} onChange={e => setShowAddCl(a => ({ ...a, phone: e.target.value }))} style={inp} /></Fl>
          <Fl l="Email"><input value={showAddCl.email || ""} onChange={e => setShowAddCl(a => ({ ...a, email: e.target.value }))} style={inp} /></Fl>
          <Fl l="Program">
            <select value={showAddCl.program || "CIP"} onChange={e => setShowAddCl(a => ({ ...a, program: e.target.value }))} style={inp}>
              <option>CIP</option><option>SROP</option>
            </select>
          </Fl>
          <Fl l="Counselor">
            <select value={showAddCl.counselor_id || ""} onChange={e => setShowAddCl(a => ({ ...a, counselor_id: Number(e.target.value) || null }))} style={inp}>
              <option value="">None</option>
              {cos.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Fl>
          <Fl l="Status">
            <select value={showAddCl.status || "Active"} onChange={e => setShowAddCl(a => ({ ...a, status: e.target.value }))} style={inp}>
              {["Active", "Waitlist", "Discharged", "Inactive"].map(s => <option key={s}>{s}</option>)}
            </select>
          </Fl>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <Fl l="Total Owed"><input type="number" step="0.01" value={showAddCl.total_owed || 0} onChange={e => setShowAddCl(a => ({ ...a, total_owed: Number(e.target.value) }))} style={inp} /></Fl>
            <Fl l="Total Paid"><input type="number" step="0.01" value={showAddCl.total_paid || 0} onChange={e => setShowAddCl(a => ({ ...a, total_paid: Number(e.target.value) }))} style={inp} /></Fl>
          </div>
          <Fl l="Contact Preference">
            <select value={showAddCl.contact_pref || "both"} onChange={e => setShowAddCl(a => ({ ...a, contact_pref: e.target.value }))} style={inp}>
              <option value="email">Email</option><option value="text">Text</option><option value="both">Both</option><option value="neither">Neither</option>
            </select>
          </Fl>
          <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
            <button onClick={async () => {
              setSaving(true);
              try {
                if (showAddCl.id) { await api.updateClient(showAddCl.id, showAddCl); st("Client updated"); }
                else { await api.createClient(showAddCl); st("Client created"); }
                setShowAddCl(null); reloadCls();
              } catch (e) { st(e.message || "Save failed", "warning"); }
              setSaving(false);
            }} style={{ ...bpP, opacity: saving ? .6 : 1 }} disabled={saving}>{saving ? "Saving..." : showAddCl.id ? "Save" : "Create"}</button>
            <button onClick={() => setShowAddCl(null)} style={bp}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* ════════════════════════════════════════ */}
      {/*  NEW INTAKE WIZARD MODAL                 */}
      {/* ════════════════════════════════════════ */}
      {showIntake && (
        <Modal title={intakeSlots === null && intakeStep === 3 ? "Schedule Intake" : `New Intake — Step ${intakeStep} of 3`} onClose={closeIntake} width={640}>
          {/* ── Step 1: Choose timing preference ── */}
          {intakeStep === 1 && (
            <div>
              <p style={{ fontSize: 13, color: "#475569", marginTop: 0, marginBottom: 14 }}>
                Find available intake appointment times. Choose how you'd like to filter the options.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                {[
                  { v: 'all', l: 'Next Available', d: 'Show all counselors with open intake slots' },
                  { v: 'day', l: 'Day Groups', d: 'Counselors whose groups are scheduled during daytime hours' },
                  { v: 'evening', l: 'Evening Groups', d: 'Counselors whose groups are scheduled in the evening' },
                ].map(opt => (
                  <button key={opt.v} onClick={() => { setIntakeFilter(opt.v); searchIntakeSlots(opt.v); }} disabled={intakeLoading}
                    style={{ padding: "14px 10px", borderRadius: 10, border: "1.5px solid #e2e8f0", background: "#fff", cursor: intakeLoading ? "wait" : "pointer", textAlign: "left", display: "flex", flexDirection: "column", gap: 4, opacity: intakeLoading ? .6 : 1 }}
                    onMouseEnter={e => !intakeLoading && (e.currentTarget.style.borderColor = "#6366f1", e.currentTarget.style.background = "#f0f0ff")}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = "#e2e8f0", e.currentTarget.style.background = "#fff")}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>{opt.l}</div>
                    <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.35 }}>{opt.d}</div>
                  </button>
                ))}
              </div>
              {intakeLoading && <div style={{ marginTop: 14, fontSize: 12, color: "#64748b", textAlign: "center" }}>Searching for available slots…</div>}
            </div>
          )}

          {/* ── Step 2: Pick a slot ── */}
          {intakeStep === 2 && intakeSlots && (
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  Filter: <strong style={{ color: "#0f172a" }}>{intakeFilter === 'all' ? 'Next Available' : intakeFilter === 'day' ? 'Day Groups' : 'Evening Groups'}</strong>
                  {intakeSlots.results.length > 0 && <> · Showing from <strong style={{ color: "#0f172a" }}>{fmtDateShort(intakeSlots.results[0].date)}</strong></>}
                </div>
                <button onClick={() => { setIntakeStep(1); setIntakeSlots(null); }} style={{ ...bp, fontSize: 11 }}>← Change Filter</button>
              </div>
              {intakeSlots.results.length === 0 && (
                <div style={{ padding: 30, textAlign: "center", color: "#94a3b8", background: "#f8fafc", borderRadius: 10 }}>
                  No available intake slots found in the next {intakeSlots.searched_days} days with this filter. Try a different filter or check counselor work hours / intake caps.
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: "55vh", overflowY: "auto" }}>
                {intakeSlots.results.map(day => (
                  <div key={day.date}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: "#475569", marginBottom: 6, padding: "4px 8px", background: "#f1f5f9", borderRadius: 5 }}>
                      {new Date(day.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
                    </div>
                    {day.counselors.map(co => (
                      <div key={co.counselor_id} style={{ marginBottom: 10, padding: 10, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, borderLeft: `4px solid ${co.color || "#6366f1"}` }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>{co.counselor_name}</div>
                          <div style={{ fontSize: 11, color: co.intake_used >= co.intake_cap ? "#dc2626" : "#059669", fontWeight: 600 }}>
                            Intakes: {co.intake_used} of {co.intake_cap} used this week
                          </div>
                        </div>
                        {co.groups && co.groups.length > 0 && (
                          <div style={{ fontSize: 10.5, color: "#64748b", marginBottom: 6 }}>
                            <strong>Groups:</strong> {co.groups.map(g => `${g.name || 'Group'} (${g.day} ${fm(g.start_hour, g.start_min || 0)})`).join(' · ')}
                          </div>
                        )}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {co.slots.map((s, si) => {
                            const isPicked = intakePicked && intakePicked.counselor_id === co.counselor_id && intakePicked.date === day.date && intakePicked.slot.start_hour === s.start_hour && intakePicked.slot.start_min === s.start_min;
                            return (
                              <button key={si} onClick={() => setIntakePicked({ counselor_id: co.counselor_id, counselor_name: co.counselor_name, date: day.date, slot: s, intake_used: co.intake_used, intake_cap: co.intake_cap })}
                                style={{ padding: "6px 10px", borderRadius: 6, border: isPicked ? "2px solid #6366f1" : "1px solid #cbd5e1", background: isPicked ? "#6366f1" : "#fff", color: isPicked ? "#fff" : "#334155", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
                                {fm(s.start_hour, s.start_min)} – {fm(s.end_hour, s.end_min)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 14 }}>
                <button onClick={() => setIntakeStep(3)} disabled={!intakePicked} style={{ ...bpP, opacity: intakePicked ? 1 : .4 }}>Next: Select Client →</button>
                <button onClick={closeIntake} style={bp}>Cancel</button>
              </div>
            </div>
          )}

          {/* ── Step 3: Existing or new client ── */}
          {intakeStep === 3 && intakePicked && (
            <div>
              <div style={{ padding: 10, background: "#f0f0ff", borderRadius: 8, marginBottom: 14, fontSize: 12 }}>
                <strong>Scheduling:</strong> {intakePicked.counselor_name} · {new Date(intakePicked.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} · {fm(intakePicked.slot.start_hour, intakePicked.slot.start_min)} – {fm(intakePicked.slot.end_hour, intakePicked.slot.end_min)}
              </div>
              <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
                <button onClick={() => setIntakeClientMode('existing')} style={{ ...bp, background: intakeClientMode === 'existing' ? '#6366f1' : '#f8fafc', color: intakeClientMode === 'existing' ? '#fff' : '#334155', border: intakeClientMode === 'existing' ? '1px solid #6366f1' : '1px solid #e2e8f0' }}>Existing Client</button>
                <button onClick={() => setIntakeClientMode('new')} style={{ ...bp, background: intakeClientMode === 'new' ? '#6366f1' : '#f8fafc', color: intakeClientMode === 'new' ? '#fff' : '#334155', border: intakeClientMode === 'new' ? '1px solid #6366f1' : '1px solid #e2e8f0' }}>New Client</button>
              </div>
              {intakeClientMode === 'existing' && (
                <Fl l="Select Client">
                  <select value={intakeExistingId} onChange={e => setIntakeExistingId(e.target.value)} style={inp}>
                    <option value="">— Choose a client —</option>
                    {cls.filter(c => c.status === 'Active' || c.status === 'Waitlist').sort((a, b) => `${a.last_name},${a.first_name}`.localeCompare(`${b.last_name},${b.first_name}`)).map(c => (
                      <option key={c.id} value={c.id}>{c.last_name}, {c.first_name} ({c.program} · {c.status})</option>
                    ))}
                  </select>
                </Fl>
              )}
              {intakeClientMode === 'new' && (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <Fl l="First Name"><input value={intakeNewClient.first_name} onChange={e => setIntakeNewClient(a => ({ ...a, first_name: e.target.value }))} style={inp} /></Fl>
                    <Fl l="Last Name"><input value={intakeNewClient.last_name} onChange={e => setIntakeNewClient(a => ({ ...a, last_name: e.target.value }))} style={inp} /></Fl>
                    <Fl l="Phone"><input value={intakeNewClient.phone} onChange={e => setIntakeNewClient(a => ({ ...a, phone: e.target.value }))} style={inp} /></Fl>
                    <Fl l="Email"><input value={intakeNewClient.email} onChange={e => setIntakeNewClient(a => ({ ...a, email: e.target.value }))} style={inp} /></Fl>
                    <Fl l="Program">
                      <select value={intakeNewClient.program} onChange={e => setIntakeNewClient(a => ({ ...a, program: e.target.value }))} style={inp}>
                        <option>CIP</option><option>SROP</option>
                      </select>
                    </Fl>
                    <Fl l="Contact Preference">
                      <select value={intakeNewClient.contact_pref} onChange={e => setIntakeNewClient(a => ({ ...a, contact_pref: e.target.value }))} style={inp}>
                        <option value="email">Email</option><option value="text">Text</option><option value="both">Both</option><option value="neither">Neither</option>
                      </select>
                    </Fl>
                    <Fl l="Total Owed"><input type="number" step="0.01" value={intakeNewClient.total_owed} onChange={e => setIntakeNewClient(a => ({ ...a, total_owed: Number(e.target.value) }))} style={inp} /></Fl>
                    <Fl l="Total Paid"><input type="number" step="0.01" value={intakeNewClient.total_paid} onChange={e => setIntakeNewClient(a => ({ ...a, total_paid: Number(e.target.value) }))} style={inp} /></Fl>
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>The new client will be assigned to {intakePicked.counselor_name}.</div>
                </div>
              )}
              <div style={{ marginTop: 14, padding: "10px 12px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                <Chk c={sendConfirmation} onChange={() => setSendConfirmation(v => !v)} label="Send appointment confirmation now (uses client's contact preference)" />
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 16 }}>
                <button onClick={confirmIntake} disabled={saving} style={{ ...bpP, opacity: saving ? .6 : 1 }}>{saving ? "Scheduling…" : "Schedule Intake"}</button>
                <button onClick={() => setIntakeStep(intakeSlots ? 2 : 1)} style={bp}>← Back</button>
                <button onClick={closeIntake} style={bp}>Cancel</button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* ════════════════════════════════════════ */}
      {/*  EDIT COUNSELOR MODAL                    */}
      {/* ════════════════════════════════════════ */}
      {showEditCo && (
        <Modal title={`Edit: ${showEditCo.name}`} onClose={() => setShowEditCo(null)} width={520}>
          <Fl l="Name"><input value={showEditCo.name || ""} onChange={e => setShowEditCo(c => ({ ...c, name: e.target.value }))} style={inp} /></Fl>
          <Fl l="Color"><input type="color" value={showEditCo.color || "#6366f1"} onChange={e => setShowEditCo(c => ({ ...c, color: e.target.value }))} /></Fl>
          <Fl l="Initials"><input value={showEditCo.init || ""} onChange={e => setShowEditCo(c => ({ ...c, init: e.target.value }))} style={{ ...inp, width: 80 }} /></Fl>
          <Fl l="Default Intake Cap">
            <select value={showEditCo.default_intake_cap ?? 3} onChange={e => setShowEditCo(c => ({ ...c, default_intake_cap: Number(e.target.value) }))} style={sl}>
              {[0, 1, 2, 3, 4, 5, 6].map(n => <option key={n}>{n}</option>)}
            </select>
          </Fl>
          <h4 style={{ fontSize: 12, fontWeight: 700, margin: "12px 0 6px", color: "#64748b" }}>Work Hours (Per Day)</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 4, marginBottom: 12 }}>
            {["Mon", "Tue", "Wed", "Thu", "Fri"].map(day => {
              const dh = showEditCo.hours?.[day] || { start: 8, end: 17 };
              return <div key={day} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", marginBottom: 2 }}>{day}</div>
                <select value={dh.start} onChange={e => setShowEditCo(c => ({ ...c, hours: { ...(c.hours || {}), [day]: { ...dh, start: Number(e.target.value) } } }))} style={{ ...sl, width: "100%", fontSize: 10, padding: 2 }}>
                  {HOURS.map(h => <option key={h} value={h}>{fm(h, 0)}</option>)}
                </select>
                <span style={{ fontSize: 9, color: "#94a3b8" }}>to</span>
                <select value={dh.end} onChange={e => setShowEditCo(c => ({ ...c, hours: { ...(c.hours || {}), [day]: { ...dh, end: Number(e.target.value) } } }))} style={{ ...sl, width: "100%", fontSize: 10, padding: 2 }}>
                  {HOURS.map(h => <option key={h} value={h}>{fm(h, 0)}</option>)}
                  <option value={23}>{fm(23, 0)}</option>
                </select>
              </div>;
            })}
          </div>
          <h4 style={{ fontSize: 12, fontWeight: 700, margin: "8px 0 6px", color: "#64748b" }}>Groups</h4>
          {["g1", "g2"].map((gk, gi) => {
            const g = showEditCo[gk] || { name: "", day: "Mon", s: 9, e: 13 };
            return <div key={gk} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 4, marginBottom: 6 }}>
              <input value={g.name || ""} placeholder={`Group ${gi + 1} name`} onChange={e => setShowEditCo(c => ({ ...c, [gk]: { ...g, name: e.target.value } }))} style={{ ...inp, fontSize: 11 }} />
              <select value={g.day || "Mon"} onChange={e => setShowEditCo(c => ({ ...c, [gk]: { ...g, day: e.target.value } }))} style={{ ...sl, fontSize: 11 }}>
                {DAYS.map(d => <option key={d}>{d}</option>)}
              </select>
              <select value={g.s ?? 9} onChange={e => setShowEditCo(c => ({ ...c, [gk]: { ...g, s: Number(e.target.value) } }))} style={{ ...sl, fontSize: 11 }}>
                {HOURS.map(h => <option key={h} value={h}>{fm(h, 0)}</option>)}
              </select>
              <select value={g.e ?? 13} onChange={e => setShowEditCo(c => ({ ...c, [gk]: { ...g, e: Number(e.target.value) } }))} style={{ ...sl, fontSize: 11 }}>
                {HOURS.map(h => <option key={h} value={h}>{fm(h, 0)}</option>)}
                <option value={23}>{fm(23, 0)}</option>
              </select>
            </div>;
          })}
          <div style={{ display: "flex", gap: 6, marginTop: 14 }}>
            <button onClick={async () => {
              setSaving(true);
              try { await api.updateCounselor(showEditCo.id, showEditCo); st("Counselor updated"); setShowEditCo(null); reloadCos(); }
              catch (e) { st("Save failed", "warning"); }
              setSaving(false);
            }} style={{ ...bpP, opacity: saving ? .6 : 1 }} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
            <button onClick={() => setShowEditCo(null)} style={bp}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* ════════════════════════════════════════ */}
      {/*  EDIT / ADD USER MODAL (owner only)      */}
      {/* ════════════════════════════════════════ */}
      {showEditUser && (
        <Modal title={showEditUser.id ? "Edit User" : "Add User"} onClose={() => setShowEditUser(null)} width={480}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <Fl l="Name"><input value={showEditUser.name || ""} onChange={e => setShowEditUser(u => ({ ...u, name: e.target.value }))} style={inp} /></Fl>
            <Fl l="Username"><input value={showEditUser.username || ""} onChange={e => setShowEditUser(u => ({ ...u, username: e.target.value }))} style={inp} /></Fl>
            <Fl l={showEditUser.id ? "New Password (blank=keep)" : "Password"}><input type="password" value={showEditUser.password || ""} onChange={e => setShowEditUser(u => ({ ...u, password: e.target.value }))} style={inp} placeholder={showEditUser.id ? "Leave blank to keep" : ""} /></Fl>
            <Fl l="Role">
              <select value={showEditUser.role || "counselor"} onChange={e => setShowEditUser(u => ({ ...u, role: e.target.value }))} style={inp}>
                <option value="counselor">Counselor</option>
                <option value="admin">Admin Staff</option>
                <option value="ops_director">Operations Director</option>
                <option value="clinical_director">Clinical Director</option>
                <option value="owner">Owner</option>
              </select>
            </Fl>
            <Fl l="Linked Counselor">
              <select value={showEditUser.counselor_id || ""} onChange={e => setShowEditUser(u => ({ ...u, counselor_id: Number(e.target.value) || null }))} style={inp}>
                <option value="">None</option>
                {cos.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Fl>
            <Fl l="Email"><input value={showEditUser.email || ""} onChange={e => setShowEditUser(u => ({ ...u, email: e.target.value }))} style={inp} /></Fl>
          </div>
          {showEditUser.id && <div style={{ marginTop: 8 }}>
            <Chk c={showEditUser.is_active !== false} onChange={() => setShowEditUser(u => ({ ...u, is_active: !u.is_active }))} label="Active" />
          </div>}
          <div style={{ display: "flex", gap: 6, marginTop: 14 }}>
            <button onClick={async () => {
              setSaving(true);
              try {
                if (showEditUser.id) { await api.updateUser(showEditUser.id, showEditUser); st("User updated"); }
                else { await api.createUser(showEditUser); st("User created"); }
                setShowEditUser(null); loadUsers();
              } catch (e) { st(e.message || "Save failed", "warning"); }
              setSaving(false);
            }} style={{ ...bpP, opacity: saving ? .6 : 1 }} disabled={saving}>{saving ? "Saving..." : showEditUser.id ? "Save" : "Create"}</button>
            <button onClick={() => setShowEditUser(null)} style={bp}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* Toast */}
      {toast && <div onClick={() => setToast(null)} style={{ position: "fixed", bottom: 18, right: 18, zIndex: 2000, background: toast.t === "warning" ? "#fef3c7" : toast.t === "info" ? "#e0e7ff" : "#dcfce7", color: toast.t === "warning" ? "#92400e" : toast.t === "info" ? "#3730a3" : "#166534", padding: "10px 18px", borderRadius: 8, fontWeight: 600, fontSize: 13, boxShadow: "0 6px 20px rgba(0,0,0,.12)", cursor: "pointer" }}>{toast.m}</div>}
    </div>
  );
}
